import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import ManageNeighborsSection from './ManageNeighborsSection'
import HoaFeeTrackerSection from './HoaFeeTrackerSection'
import SalesSection from './SalesSection'
import { useSearchParams } from 'react-router-dom'
import PaywallModal from '../components/PaywallModal'
import { collection, query, where, getDocs, doc, updateDoc, addDoc, getDoc, deleteDoc, serverTimestamp, Timestamp, orderBy } from 'firebase/firestore'
import { db } from '../utils/firebase'
import { renderAnnouncementContent } from '../utils/announcementContent'
import { formatPhoneNumber } from '../utils/phone'
import { TypeSelectorModal, CreateAnnouncementModal } from '../components/AnnouncementModals'
import { 
  Shield, 
  User,
  Users, 
  MessageSquare, 
  Megaphone,
  Flag,
  UserPlus,
  CreditCard,
  ChevronRight,
  Loader,
  Check,
  X,
  Copy,
  Plus,
  Calendar,
  AlertCircle,
  ExternalLink,
  Receipt,
  Home,
  Search,
  Key,
  ArrowLeft,
  Info,
  UserX,
  AlertTriangle,
  MapPin,
  Clock,
  Trash2,
  Edit,
  Star,
  DollarSign,
  Send,
  Globe,
  MoreVertical,
  FileText,
  Upload,
  Mail,
  Tag,
  Phone,
  Link as LinkIcon,
  Zap,
} from 'lucide-react'
import './AdminPage.css'
import { loadStripe } from '@stripe/stripe-js'
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { storage } from '../utils/firebase'
import ImageCropperModal from '../components/ImageCropperModal'

const stripePromise = loadStripe('pk_live_cCKvSpZ6D0nw2puhNMkbC71J')

function AdminPage() {
const { userProfile, isSubscriptionActive } = useAuth()
const [searchParams] = useSearchParams()
const [activeSection, setActiveSection] = useState('addresses')

// Sections blocked when subscription is inactive (subscription section stays accessible)
const blockedSections = ['addresses', 'users', 'households', 'invitations', 'flags', 'announcements']
const showAdminPaywall = !isSubscriptionActive() && blockedSections.includes(activeSection)
  const [stats, setStats] = useState(null)
  const [neighborhoodName, setNeighborhoodName] = useState('')
  const [loading, setLoading] = useState(true)
  const [flagCount, setFlagCount] = useState(0)

  useEffect(() => {
    if (userProfile) {
      loadStats()
    }
  }, [userProfile])

  // Handle URL parameter for direct navigation to subscription
  useEffect(() => {
    const section = searchParams.get('section')
    if (section) {
      setActiveSection(section)
    }
  }, [searchParams])

  const loadStats = async () => {
    try {
      setLoading(true)
      
      // Load neighborhood name
      const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId))
      if (neighborhoodDoc.exists()) {
        setNeighborhoodName(neighborhoodDoc.data().name || '')
      }
      
      // Load users count
      const usersQuery = query(
        collection(db, 'users'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      )
      const usersSnap = await getDocs(usersQuery)
      
      // Load invitations count
      const invitationsQuery = query(
        collection(db, 'invitations'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      )
      const invitationsSnap = await getDocs(invitationsQuery)
      
      const unusedInvitations = invitationsSnap.docs.filter(doc => !doc.data().isUsed).length
      
      // Load discussions count
      const discussionsQuery = query(
        collection(db, 'discussions'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      )
      const discussionsSnap = await getDocs(discussionsQuery)
      
      // Load announcements count
      const announcementsQuery = query(
        collection(db, 'announcements'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      )
      const announcementsSnap = await getDocs(announcementsQuery)

      // Load unresolved flags count
      const flagsQuery = query(
        collection(db, 'content_flags'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      )
      const flagsSnap = await getDocs(flagsQuery)
      const unresolvedCount = flagsSnap.docs.filter(d => d.data().status === 'pending').length
      setFlagCount(unresolvedCount)

      setStats({
        totalUsers: usersSnap.size,
        totalInvitations: invitationsSnap.size,
        unusedInvitations,
        totalDiscussions: discussionsSnap.size,
        totalAnnouncements: announcementsSnap.size
      })
    } catch (error) {
      console.error('Error loading stats:', error)
    } finally {
      setLoading(false)
    }
  }

 const menuItems = [
    // Neighborhood Profile leads the sidebar since it's the settings
    // surface admins hit first when setting up a neighborhood.
    { id: 'resources', label: 'Neighborhood Profile', icon: Home },
    { id: 'addresses', label: 'Manage Households', icon: MapPin },
    { id: 'flags', label: 'Flagged Content', icon: Flag },
    // Manage Discussions sits directly above Manage Announcements to
    // match the ordering already established on iOS (ViewCommunitiesAdminView
    // above ViewAnnouncementsAdminView) and Android. Regular admins and
    // board members can view and delete discussions in their own
    // neighborhood; super admins get the broader All Discussions surface
    // farther down the sidebar for cross-neighborhood management.
    { id: 'discussions', label: 'Manage Discussions', icon: MessageSquare },
    // Discussion categories are per-neighborhood. Sits directly under
    // Manage Discussions so admins find them together.
    { id: 'categories', label: 'Manage Categories', icon: Tag },
    { id: 'announcements', label: 'Manage Announcements', icon: Megaphone },
    { id: 'events', label: 'Manage Events', icon: Calendar },
    { id: 'hoa-fees', label: 'HOA Payment Tracker', icon: DollarSign },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    // Super Admin Only Items
    ...(userProfile?.isSuperAdmin ? [
      { id: 'divider1', label: 'Super Admin', divider: true },
      { id: 'sa-discussions', label: 'All Discussions', icon: MessageSquare },
      { id: 'sa-announcements', label: 'All Announcements', icon: Megaphone },
      { id: 'sa-directories', label: 'All Directories', icon: Users },
      { id: 'sa-sales', label: 'Sales', icon: Send },
    ] : [])
  ]

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Admin Panel</h1>
        {(neighborhoodName || userProfile?.neighborhoodId) && (
          <p className="admin-page-subtitle">
            {neighborhoodName || userProfile?.neighborhoodId}
          </p>
        )}
      </header>

      <div className="admin-layout">
        {/* Sidebar */}
        <aside className="admin-sidebar" data-walk="admin-sidebar">
          <nav>
            {menuItems.map(item => (
              item.divider ? (
                <div key={item.id} className="menu-divider">
                  <span>{item.label}</span>
                </div>
              ) : (
                <button
                  key={item.id}
                  className={`menu-item ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(item.id)}
                  data-walk={
                    item.id === 'resources' ? 'admin-menu-neighborhood-profile' :
                    item.id === 'addresses' ? 'admin-menu-households' :
                    item.id === 'discussions' ? 'admin-menu-discussions' :
                    item.id === 'categories' ? 'admin-menu-categories' :
                    item.id === 'events' ? 'admin-menu-events' :
                    item.id === 'hoa-fees' ? 'admin-menu-hoa' :
                    item.id === 'subscription' ? 'admin-menu-subscription' :
                    undefined
                  }
                >
                  <item.icon size={18} />
                  <span>{item.label}</span>
                  {item.id === 'flags' && flagCount > 0 && (
                    <span className="flag-count-badge">{flagCount}</span>
                  )}
                  <ChevronRight size={16} className="chevron" />
                </button>
              )
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="admin-content">
          {showAdminPaywall && <PaywallModal isAdmin={true} />}

          {activeSection === 'users' && (
            <UsersSection userProfile={userProfile} />
          )}
          {activeSection === 'addresses' && (
            <ManageNeighborsSection userProfile={userProfile} />
          )}
          {activeSection === 'flags' && (
            <FlagsSection userProfile={userProfile} />
          )}
          {activeSection === 'discussions' && (
            <ManageDiscussionsSection userProfile={userProfile} />
          )}
          {activeSection === 'categories' && (
            <ManageCategoriesSection userProfile={userProfile} />
          )}
          {activeSection === 'announcements' && (
            <AnnouncementsSection userProfile={userProfile} />
          )}
          {activeSection === 'subscription' && (
            <SubscriptionSection userProfile={userProfile} />
          )}
          {activeSection === 'resources' && (
            <NeighborhoodProfileSection userProfile={userProfile} />
          )}
          {activeSection === 'events' && (
            <ManageEventsSection userProfile={userProfile} />
          )}
          {activeSection === 'hoa-fees' && (
            <HoaFeeTrackerSection userProfile={userProfile} />
          )}
          {activeSection === 'sa-discussions' && userProfile?.isSuperAdmin && (
            <SuperAdminDiscussionsSection />
          )}
          {activeSection === 'sa-announcements' && userProfile?.isSuperAdmin && (
            <SuperAdminAnnouncementsSection />
          )}
          {activeSection === 'sa-directories' && userProfile?.isSuperAdmin && (
            <SuperAdminDirectoriesSection />
          )}
          {activeSection === 'sa-sales' && userProfile?.isSuperAdmin && (
            <SalesSection userProfile={userProfile} />
          )}
        </main>
      </div>
    </div>
  )
}

// ============ Overview Section ============
function OverviewSection({ stats, loading }) {
  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading statistics...</p>
      </div>
    )
  }

  return (
    <div className="section">
      <h2>Overview</h2>
      <div className="stats-grid">
        <div className="stat-card">
          <Users size={24} />
          <div className="stat-value">{stats?.totalUsers || 0}</div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="stat-card">
          <UserPlus size={24} />
          <div className="stat-value">{stats?.unusedInvitations || 0}</div>
          <div className="stat-label">Unused Invitations</div>
        </div>
        <div className="stat-card">
          <MessageSquare size={24} />
          <div className="stat-value">{stats?.totalDiscussions || 0}</div>
          <div className="stat-label">Discussions</div>
        </div>
        <div className="stat-card">
          <Megaphone size={24} />
          <div className="stat-value">{stats?.totalAnnouncements || 0}</div>
          <div className="stat-label">Announcements</div>
        </div>
      </div>
    </div>
  )
}

// ============ Users Section ============
function UsersSection({ userProfile }) {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [openMenuId, setOpenMenuId] = useState(null)

  useEffect(() => {
    loadUsers()
  }, [userProfile])

  useEffect(() => {
    // Close menu when clicking outside
    const handleClickOutside = () => setOpenMenuId(null)
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      
      // Load neighborhood name
      const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId))
      if (neighborhoodDoc.exists()) {
        setNeighborhoodName(neighborhoodDoc.data().name || '')
      }
      const usersQuery = query(
        collection(db, 'users'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      )
      const snap = await getDocs(usersQuery)
      const usersData = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      // Sort by name
      usersData.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      setUsers(usersData)
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleBoardMember = async (userId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isBoardMember: !currentStatus
      })
      setOpenMenuId(null)
      loadUsers()
    } catch (error) {
      console.error('Error updating user:', error)
    }
  }

  const toggleAdmin = async (userId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isAdmin: !currentStatus
      })
      setOpenMenuId(null)
      loadUsers()
    } catch (error) {
      console.error('Error updating user:', error)
    }
  }

  const handleMenuClick = (e, userId) => {
    e.stopPropagation()
    setOpenMenuId(openMenuId === userId ? null : userId)
  }

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.address?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getRole = (user) => {
    if (user.isSuperAdmin) return { label: 'Super Admin', color: 'purple' }
    if (user.isAdmin) {
      const title = (user.adminTitle || '').trim()
      return {
        label: title ? `Admin · ${title}` : 'Admin',
        color: 'green',
      }
    }
    if (user.isBoardMember) {
      const title = (user.boardTitle || '').trim()
      return {
        label: title ? `Board Member · ${title}` : 'Board Member',
        color: 'blue',
      }
    }
    return null
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading users...</p>
      </div>
    )
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2>Manage Users</h2>
        <span className="user-count">{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}</span>
      </div>
      
      <div className="search-box">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="clear-btn" onClick={() => setSearchQuery('')}>
            <X size={16} />
          </button>
        )}
      </div>

      <div className="users-list-table">
        <div className="users-list-header">
          <span className="col-user">User</span>
          <span className="col-email">Email</span>
          <span className="col-address">Address</span>
          <span className="col-role">Role</span>
          <span className="col-actions"></span>
        </div>
        
        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <h3>No users found</h3>
            <p>{searchQuery ? 'Try adjusting your search' : 'No users in this neighborhood yet.'}</p>
          </div>
        ) : (
          filteredUsers.map(user => {
            const role = getRole(user)
            return (
              <div key={user.id} className="users-list-row">
                <div className="col-user">
                  <div className="user-avatar small">
                    {user.profilePhotoURL ? (
                      <img src={user.profilePhotoURL} alt={user.name} />
                    ) : (
                      <span>{user.name?.charAt(0).toUpperCase() || '?'}</span>
                    )}
                  </div>
                  <span className="user-name">{user.name || 'Unknown'}</span>
                </div>
                
                <div className="col-email">
                  <span>{user.email || '-'}</span>
                </div>
                
                <div className="col-address">
                  <span>{user.address || '-'}</span>
                </div>
                
                <div className="col-role">
                  {role ? (
                    <span className={`role-badge ${role.color}`}>{role.label}</span>
                  ) : (
                    <span className="role-badge default">Neighbor</span>
                  )}
                </div>
                
                <div className="col-actions">
                  {!user.isSuperAdmin && (
                    <div className="dropdown-container">
                      <button 
                        className="menu-btn"
                        onClick={(e) => handleMenuClick(e, user.id)}
                      >
                        <MoreVertical size={18} />
                      </button>
                      
                      {openMenuId === user.id && (
                        <div className="dropdown-menu" onClick={e => e.stopPropagation()}>
                          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleBoardMember(user.id, user.isBoardMember); }}>
                            {user.isBoardMember ? 'Remove Board Member' : 'Make Board Member'}
                          </button>
                          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleAdmin(user.id, user.isAdmin); }}>
                            {user.isAdmin ? 'Remove Admin' : 'Make Neighborhood Admin'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}


// ============ Households Section ============
function HouseholdsSection({ userProfile }) {
  const [households, setHouseholds] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedHousehold, setSelectedHousehold] = useState(null)

  useEffect(() => {
    loadHouseholds()
  }, [userProfile])

  const loadHouseholds = async () => {
    try {
      setLoading(true)
      
      // Load neighborhood name
      const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId))
      if (neighborhoodDoc.exists()) {
        setNeighborhoodName(neighborhoodDoc.data().name || '')
      }
      
      // Get all users in this neighborhood
      const usersQuery = query(
        collection(db, 'users'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      )
      const usersSnap = await getDocs(usersQuery)
      
      // Group users by address (household)
      const householdMap = new Map()
      
      usersSnap.docs.forEach(doc => {
        const data = doc.data()
        const address = data.address || 'Unknown Address'
        
        if (!householdMap.has(address)) {
          householdMap.set(address, {
            address,
            neighborhoodId: data.neighborhoodId,
            members: [],
            invitationCode: data.invitationCode,
            joinedDate: data.joinedDate?.toDate() || new Date(),
            pendingInvites: []
          })
        }
        
        const household = householdMap.get(address)
        household.members.push({
          id: doc.id,
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          joinedDate: data.joinedDate?.toDate() || new Date(),
          isPrimary: data.isPrimaryResident || false,
          isAdmin: data.isAdmin || data.isBoardMember || false,
          profilePhotoURL: data.profilePhotoURL
        })
        
        // Update earliest join date for household
        const memberJoinDate = data.joinedDate?.toDate()
        if (memberJoinDate && memberJoinDate < household.joinedDate) {
          household.joinedDate = memberJoinDate
        }
        
        // Keep track of invitation code
        if (data.invitationCode) {
          household.invitationCode = data.invitationCode
        }
      })

      // Load pending invites for each address
      const invitesQuery = query(
        collection(db, 'householdInvites'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        where('status', '==', 'pending')
      )
      const invitesSnap = await getDocs(invitesQuery)
      
      invitesSnap.docs.forEach(doc => {
        const data = doc.data()
        const address = data.address
        
        if (householdMap.has(address)) {
          householdMap.get(address).pendingInvites.push({
            id: doc.id,
            name: data.inviteeName || '',
            email: data.inviteeEmail || '',
            sentDate: data.createdDate?.toDate() || new Date(),
            expirationDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
          })
        }
      })

      // Convert to array and determine status
      const householdArray = Array.from(householdMap.values()).map(h => {
        // Sort members by join date, earliest first
        h.members.sort((a, b) => a.joinedDate - b.joinedDate)
        
        // Determine primary (marked or earliest)
        const hasPrimaryMarked = h.members.some(m => m.isPrimary)
        h.members = h.members.map((m, idx) => ({
          ...m,
          isPrimary: hasPrimaryMarked ? m.isPrimary : idx === 0
        }))
        
        // Determine status
        let status = 'active'
        if (h.members.length === 0) {
          status = 'vacant'
        } else if (h.pendingInvites.length > 0 && h.members.length === 0) {
          status = 'pending'
        }
        
        return {
          ...h,
          status,
          primaryMember: h.members.find(m => m.isPrimary) || h.members[0],
          hasPendingInvites: h.pendingInvites.length > 0
        }
      })

      // Sort by address
      householdArray.sort((a, b) => a.address.localeCompare(b.address))
      
      setHouseholds(householdArray)
    } catch (error) {
      console.error('Error loading households:', error)
    } finally {
      setLoading(false)
    }
  }

  // Filter households
  const filteredHouseholds = households.filter(h => {
    // Status filter
    if (selectedStatus !== 'all' && h.status !== selectedStatus) {
      return false
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      const addressMatch = h.address.toLowerCase().includes(q)
      const memberMatch = h.members.some(m => m.name.toLowerCase().includes(q))
      return addressMatch || memberMatch
    }
    
    return true
  })

  const extractStreetAddress = (fullAddress) => {
    const parts = fullAddress.split(',')
    return parts[0]?.trim() || fullAddress
  }

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="status-badge active">Active</span>
      case 'pending':
        return <span className="status-badge pending">Pending</span>
      case 'vacant':
        return <span className="status-badge vacant">Vacant</span>
      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading households...</p>
      </div>
    )
  }

  // If a household is selected, show detail view
  if (selectedHousehold) {
    return (
      <HouseholdDetailView 
        household={selectedHousehold}
        onBack={() => {
          setSelectedHousehold(null)
          loadHouseholds() // Refresh data
        }}
        userProfile={userProfile}
      />
    )
  }

  return (
    <div className="section">
      <h2>Manage Households</h2>
      
      {/* Filters */}
      <div className="households-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search addresses or names..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-btn" onClick={() => setSearchQuery('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="status-filters">
          <button 
            className={`filter-chip ${selectedStatus === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('all')}
          >
            All
          </button>
          <button 
            className={`filter-chip ${selectedStatus === 'active' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('active')}
          >
            Active
          </button>
          <button 
            className={`filter-chip ${selectedStatus === 'pending' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('pending')}
          >
            Pending
          </button>
          <button 
            className={`filter-chip ${selectedStatus === 'vacant' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('vacant')}
          >
            Vacant
          </button>
        </div>
      </div>

      {/* Results Count */}
      <p className="results-count">
        {filteredHouseholds.length} {filteredHouseholds.length === 1 ? 'household' : 'households'}
      </p>

      {/* Households List */}
      {filteredHouseholds.length === 0 ? (
        <div className="empty-state">
          <Home size={48} />
          <h3>No households found</h3>
          <p>
            {searchQuery ? 'Try a different search' : 'No households to display'}
          </p>
        </div>
      ) : (
        <div className="households-list">
          {filteredHouseholds.map(household => (
            <div 
              key={household.address} 
              className="household-card"
              onClick={() => setSelectedHousehold(household)}
            >
              <div className="household-card-header">
                <h3>{extractStreetAddress(household.address)}</h3>
                {getStatusBadge(household.status)}
              </div>
              
              <div className="household-card-details">
                <div className="detail-row">
                  <MapPin size={16} />
                  <span>{household.neighborhoodId}</span>
                </div>
                
                <div className="detail-row">
                  <Users size={16} />
                  <span>{household.members.length}/5 members</span>
                  {household.primaryMember && (
                    <>
                      <span className="separator">•</span>
                      <span className="primary-name">{household.primaryMember.name}</span>
                    </>
                  )}
                </div>
                
                {household.invitationCode && (
                  <div className="detail-row invitation-code">
                    <Key size={16} />
                    <span>Code: {household.invitationCode}</span>
                  </div>
                )}
                
                <div className="household-card-footer">
                  <span className="joined-date">Joined: {formatDate(household.joinedDate)}</span>
                  {household.hasPendingInvites && (
                    <span className="pending-badge">
                      <Clock size={14} />
                      Pending invites
                    </span>
                  )}
                </div>
              </div>
              
              <ChevronRight size={20} className="chevron" />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ============ Household Detail View ============
function HouseholdDetailView({ household, onBack, userProfile }) {
  const [showDeleteHouseholdModal, setShowDeleteHouseholdModal] = useState(false)
  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  }

  const formatRelativeDate = (date) => {
    const now = new Date()
    const diffMs = date - now
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffDays > 1) return `in ${diffDays} days`
    if (diffDays === 1) return 'tomorrow'
    if (diffDays === 0) return 'today'
    if (diffDays === -1) return 'yesterday'
    return `${Math.abs(diffDays)} days ago`
  }

  const handleDeleteMember = async (member) => {
    try {
      setDeleting(true)
      await deleteDoc(doc(db, 'users', member.id))
      setShowDeleteMemberModal(null)
      onBack() // Refresh
    } catch (error) {
      console.error('Error deleting member:', error)
      alert('Failed to delete member')
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteHousehold = async () => {
    try {
      setDeleting(true)
      
      // Delete all members
      for (const member of household.members) {
        await deleteDoc(doc(db, 'users', member.id))
      }
      
      // Delete pending invites
      for (const invite of household.pendingInvites) {
        await deleteDoc(doc(db, 'householdInvites', invite.id))
      }

      // Also free up the invitation code if it exists
      if (household.invitationCode) {
        const invitationsQuery = query(
          collection(db, 'invitations'),
          where('code', '==', household.invitationCode)
        )
        const invitationsSnap = await getDocs(invitationsQuery)
        for (const docSnap of invitationsSnap.docs) {
          await updateDoc(docSnap.ref, { isUsed: false })
        }
      }
      
      setShowDeleteHouseholdModal(false)
      onBack()
    } catch (error) {
      console.error('Error deleting household:', error)
      alert('Failed to delete household')
    } finally {
      setDeleting(false)
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="status-badge active">Active</span>
      case 'pending':
        return <span className="status-badge pending">Pending</span>
      case 'vacant':
        return <span className="status-badge vacant">Vacant</span>
      default:
        return null
    }
  }

  return (
    <div className="section household-detail">
      {/* Back Button */}
      <button className="back-btn" onClick={onBack}>
        <ArrowLeft size={18} />
        Back to Households
      </button>

      <h2>Household Details</h2>

      {/* Household Info Card */}
      <div className="info-card">
        <h3>Household Information</h3>
        
        <div className="info-row">
          <Home size={18} />
          <div>
            <span className="label">Address</span>
            <span className="value">{household.address}</span>
          </div>
        </div>
        
        <div className="info-row">
          <MapPin size={18} />
          <div>
            <span className="label">Neighborhood</span>
            <span className="value">{household.neighborhoodId}</span>
          </div>
        </div>
        
        <div className="info-row">
          <Calendar size={18} />
          <div>
            <span className="label">Joined</span>
            <span className="value">{formatDate(household.joinedDate)}</span>
          </div>
        </div>
        
        <div className="info-row">
          <Info size={18} />
          <div>
            <span className="label">Status</span>
            {getStatusBadge(household.status)}
          </div>
        </div>
        
        {household.invitationCode && (
          <div className="info-row">
            <Key size={18} />
            <div>
              <span className="label">Invitation Code</span>
              <span className="value code">{household.invitationCode}</span>
            </div>
          </div>
        )}
      </div>

      {/* Members Section */}
      <h3 className="section-title">
        Household Members ({household.members.length}/5)
      </h3>
      
      {household.members.length === 0 ? (
        <div className="empty-card">
          <UserX size={48} />
          <p>No active members</p>
        </div>
      ) : (
        <div className="members-list">
          {household.members.map(member => (
            <div key={member.id} className="member-card">
              <div className="member-avatar">
                {member.profilePhotoURL ? (
                  <img src={member.profilePhotoURL} alt={member.name} />
                ) : (
                  <span>{member.name.charAt(0).toUpperCase()}</span>
                )}
              </div>
              
              <div className="member-info">
                <div className="member-name">
                  {member.name}
                  {member.isPrimary && <Star size={14} className="star-icon" />}
                  {member.isAdmin && <Shield size={14} className="admin-icon" />}
                </div>
                <div className="member-email">{member.email}</div>
                {member.phone && <div className="member-phone">{formatPhoneNumber(member.phone)}</div>}
                <div className="member-joined">Joined {formatDate(member.joinedDate)}</div>
              </div>
              
              <button 
                className="delete-btn"
                onClick={() => setShowDeleteMemberModal(member)}
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pending Invites Section */}
      {household.pendingInvites.length > 0 && (
        <>
          <h3 className="section-title">
            Pending Invitations ({household.pendingInvites.length})
          </h3>
          
          <div className="invites-list">
            {household.pendingInvites.map(invite => (
              <div key={invite.id} className="invite-card">
                <Clock size={24} className="invite-icon" />
                <div className="invite-info">
                  <div className="invite-name">{invite.name || invite.email}</div>
                  <div className="invite-email">{invite.email}</div>
                  <div className="invite-dates">
                    Sent {formatRelativeDate(invite.sentDate)} • Expires {formatRelativeDate(invite.expirationDate)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Danger Zone */}
      <h3 className="section-title danger">Danger Zone</h3>
      
      <div className="danger-card">
        <h4>Delete Entire Household</h4>
        <p>
          This will permanently delete all {household.members.length} members and free up the invitation code. 
          This action cannot be undone.
        </p>
        <button 
          className="danger-btn"
          onClick={() => setShowDeleteHouseholdModal(true)}
        >
          <Trash2 size={18} />
          Delete Household
        </button>
      </div>

      {/* Delete Member Modal */}
      {showDeleteMemberModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteMemberModal(null)}>
          <div className="modal-content confirm-modal" onClick={e => e.stopPropagation()}>
            <AlertTriangle size={48} className="warning-icon" />
            <h3>Delete Member?</h3>
            <p>Are you sure you want to delete {showDeleteMemberModal.name} from this household?</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setShowDeleteMemberModal(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="danger-btn" 
                onClick={() => handleDeleteMember(showDeleteMemberModal)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Household Modal */}
      {showDeleteHouseholdModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteHouseholdModal(false)}>
          <div className="modal-content confirm-modal" onClick={e => e.stopPropagation()}>
            <AlertTriangle size={48} className="warning-icon" />
            <h3>Delete Entire Household?</h3>
            <p>
              This will permanently delete all {household.members.length} members and free up the invitation code. 
              This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setShowDeleteHouseholdModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="danger-btn" 
                onClick={handleDeleteHousehold}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Household'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============ Invitations Section (Improved with Full Address) ============

function InvitationsSection({ userProfile }) {
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [copiedCode, setCopiedCode] = useState(null)
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all') // all, available, used
  const [searchQuery, setSearchQuery] = useState('')
  
  // Create invitation modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createMode, setCreateMode] = useState('single') // single or bulk
  const [createError, setCreateError] = useState('')
  
  // Single invite fields
  const [singleAddress1, setSingleAddress1] = useState('')
  const [singleAddress2, setSingleAddress2] = useState('')
  const [singleCity, setSingleCity] = useState('')
  const [singleState, setSingleState] = useState('')
  const [singleZip, setSingleZip] = useState('')
  
  // Bulk invite fields
  const [bulkAddresses, setBulkAddresses] = useState([
    { address1: '', address2: '', city: '', state: '', zip: '' }
  ])
  const [applyToAllCity, setApplyToAllCity] = useState('')
  const [applyToAllState, setApplyToAllState] = useState('')
  const [applyToAllZip, setApplyToAllZip] = useState('')

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ]

  useEffect(() => {
    loadInvitations()
  }, [userProfile])

  const loadInvitations = async () => {
    try {
      setLoading(true)
      
      // Load neighborhood name
      const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId))
      if (neighborhoodDoc.exists()) {
        setNeighborhoodName(neighborhoodDoc.data().name || '')
      }
      const invitationsQuery = query(
        collection(db, 'invitations'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      )
      const snap = await getDocs(invitationsQuery)
      const invitationsData = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null
      }))
      setInvitations(invitationsData.sort((a, b) => {
        const dateA = a.createdAt || new Date(0)
        const dateB = b.createdAt || new Date(0)
        return dateB - dateA
      }))
    } catch (error) {
      console.error('Error loading invitations:', error)
    } finally {
      setLoading(false)
    }
  }

  const generateInviteCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
    return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
  }

  const formatFullAddress = (address1, address2, city, state, zip) => {
    let address = address1
    if (address2) address += `, ${address2}`
    address += `, ${city}, ${state} ${zip}`
    return address
  }

  const validateZip = (zip) => {
    return /^\d{5}$/.test(zip)
  }

  const handleCreateInvitations = async () => {
    setCreateError('')
    
    let addressList = []
    
    if (createMode === 'single') {
      // Validate single invite
      if (!singleAddress1.trim()) {
        setCreateError('Please enter a street address')
        return
      }
      if (!singleCity.trim()) {
        setCreateError('Please enter a city')
        return
      }
      if (!singleState) {
        setCreateError('Please select a state')
        return
      }
      if (!validateZip(singleZip)) {
        setCreateError('Please enter a valid 5-digit ZIP code')
        return
      }
      
      addressList = [{
        address1: singleAddress1.trim(),
        address2: singleAddress2.trim(),
        city: singleCity.trim(),
        state: singleState,
        zip: singleZip
      }]
    } else {
      // Validate bulk invites
      const validAddresses = bulkAddresses.filter(a => a.address1.trim())
      
      if (validAddresses.length === 0) {
        setCreateError('Please enter at least one address')
        return
      }
      
      for (let i = 0; i < validAddresses.length; i++) {
        const addr = validAddresses[i]
        if (!addr.city.trim()) {
          setCreateError(`Row ${i + 1}: Please enter a city`)
          return
        }
        if (!addr.state) {
          setCreateError(`Row ${i + 1}: Please select a state`)
          return
        }
        if (!validateZip(addr.zip)) {
          setCreateError(`Row ${i + 1}: Please enter a valid 5-digit ZIP code`)
          return
        }
      }
      
      addressList = validAddresses.map(a => ({
        address1: a.address1.trim(),
        address2: a.address2.trim(),
        city: a.city.trim(),
        state: a.state,
        zip: a.zip
      }))
    }
    
    try {
      setGenerating(true)
      
      // Create invitations for each address
      for (const addr of addressList) {
        const code = generateInviteCode()
        const fullAddress = formatFullAddress(addr.address1, addr.address2, addr.city, addr.state, addr.zip)
        
        await addDoc(collection(db, 'invitations'), {
          code,
          address: fullAddress,
          address1: addr.address1,
          address2: addr.address2 || '',
          city: addr.city,
          state: addr.state,
          zip: addr.zip,
          neighborhoodId: userProfile.neighborhoodId,
          createdBy: userProfile.uid,
          createdByName: userProfile.name || userProfile.email,
          createdAt: serverTimestamp(),
          isUsed: false
        })
      }
      
      // Reset form and close modal
      resetForm()
      setShowCreateModal(false)
      loadInvitations()
    } catch (error) {
      console.error('Error generating invitations:', error)
      setCreateError('Failed to create invitations. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const resetForm = () => {
    setSingleAddress1('')
    setSingleAddress2('')
    setSingleCity('')
    setSingleState('')
    setSingleZip('')
    setBulkAddresses([{ address1: '', address2: '', city: '', state: '', zip: '' }])
    setApplyToAllCity('')
    setApplyToAllState('')
    setApplyToAllZip('')
    setCreateError('')
  }

  const addBulkRow = () => {
    setBulkAddresses([...bulkAddresses, { 
      address1: '', 
      address2: '', 
      city: applyToAllCity, 
      state: applyToAllState, 
      zip: applyToAllZip 
    }])
  }

  const removeBulkRow = (index) => {
    if (bulkAddresses.length > 1) {
      setBulkAddresses(bulkAddresses.filter((_, i) => i !== index))
    }
  }

  const updateBulkAddress = (index, field, value) => {
    const updated = [...bulkAddresses]
    updated[index][field] = value
    setBulkAddresses(updated)
  }

  const applyToAll = () => {
    const updated = bulkAddresses.map(addr => ({
      ...addr,
      city: applyToAllCity || addr.city,
      state: applyToAllState || addr.state,
      zip: applyToAllZip || addr.zip
    }))
    setBulkAddresses(updated)
  }

  const copyCode = (code) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const copyAllAvailable = () => {
    const availableCodes = filteredInvitations
      .filter(inv => !inv.isUsed)
      .map(inv => `${inv.code} - ${inv.address}`)
      .join('\n')
    navigator.clipboard.writeText(availableCodes)
    setCopiedCode('all')
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const formatDate = (date) => {
    if (!date) return 'Unknown'
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  // Filter invitations
  const filteredInvitations = invitations.filter(invite => {
    // Status filter
    if (statusFilter === 'available' && invite.isUsed) return false
    if (statusFilter === 'used' && !invite.isUsed) return false
    
    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase()
      return (
        invite.code?.toLowerCase().includes(search) ||
        invite.address?.toLowerCase().includes(search)
      )
    }
    
    return true
  })

  // Stats
  const totalCount = invitations.length
  const availableCount = invitations.filter(i => !i.isUsed).length
  const usedCount = invitations.filter(i => i.isUsed).length

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading invitations...</p>
      </div>
    )
  }

  return (
    <div className="section invitations-section">
      <div className="section-header">
        <h2>Invitations</h2>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} />
          <span>Create Invitation</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="invitation-stats">
        <div 
          className={`stat-pill ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          <span className="stat-count">{totalCount}</span>
          <span className="stat-label">Total</span>
        </div>
        <div 
          className={`stat-pill available ${statusFilter === 'available' ? 'active' : ''}`}
          onClick={() => setStatusFilter('available')}
        >
          <span className="stat-count">{availableCount}</span>
          <span className="stat-label">Available</span>
        </div>
        <div 
          className={`stat-pill used ${statusFilter === 'used' ? 'active' : ''}`}
          onClick={() => setStatusFilter('used')}
        >
          <span className="stat-count">{usedCount}</span>
          <span className="stat-label">Used</span>
        </div>
      </div>

      {/* Search and Actions Bar */}
      <div className="invitations-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by code or address..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-btn" onClick={() => setSearchQuery('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        {availableCount > 0 && (
          <button className="btn-secondary" onClick={copyAllAvailable}>
            {copiedCode === 'all' ? <Check size={18} /> : <Copy size={18} />}
            <span>{copiedCode === 'all' ? 'Copied!' : 'Copy All Available'}</span>
          </button>
        )}
      </div>

      {/* Invitations List */}
      <div className="invitations-list-improved">
        {filteredInvitations.length === 0 ? (
          <div className="empty-state">
            <UserPlus size={48} />
            <h3>No invitations found</h3>
            <p>
              {searchQuery 
                ? 'Try adjusting your search'
                : statusFilter !== 'all'
                  ? `No ${statusFilter} invitations`
                  : 'Create your first invitation to get started'}
            </p>
          </div>
        ) : (
          filteredInvitations.map(invite => (
            <div key={invite.id} className={`invitation-card-improved ${invite.isUsed ? 'used' : 'available'}`}>
              <div className="invitation-main">
                <div className="invitation-code-display">
                  <span className="code">{invite.code}</span>
                  <button 
                    className="copy-btn-small"
                    onClick={() => copyCode(invite.code)}
                    disabled={invite.isUsed}
                    title={invite.isUsed ? 'Code already used' : 'Copy code'}
                  >
                    {copiedCode === invite.code ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <div className="invitation-address-display">
                  <MapPin size={14} />
                  <span>{invite.address}</span>
                </div>
              </div>
              
              <div className="invitation-meta">
                <div className="meta-item">
                  <Calendar size={14} />
                  <span>{formatDate(invite.createdAt)}</span>
                </div>
                <div className={`status-badge ${invite.isUsed ? 'used' : 'available'}`}>
                  {invite.isUsed ? 'Used' : 'Available'}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Invitation Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Invitation</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              {/* Mode Toggle */}
              <div className="mode-toggle">
                <button 
                  className={`mode-btn ${createMode === 'single' ? 'active' : ''}`}
                  onClick={() => setCreateMode('single')}
                >
                  <User size={18} />
                  <span>Single Invite</span>
                </button>
                <button 
                  className={`mode-btn ${createMode === 'bulk' ? 'active' : ''}`}
                  onClick={() => setCreateMode('bulk')}
                >
                  <Users size={18} />
                  <span>Bulk Invite</span>
                </button>
              </div>

              {createError && (
                <div className="form-error">{createError}</div>
              )}

              {createMode === 'single' ? (
                /* Single Invite Form */
                <div className="single-invite-form">
                  <div className="form-group">
                    <label htmlFor="address1">Street Address *</label>
                    <input
                      id="address1"
                      type="text"
                      placeholder="e.g., 123 Main Street"
                      value={singleAddress1}
                      onChange={e => setSingleAddress1(e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="address2">Address Line 2 (Optional)</label>
                    <input
                      id="address2"
                      type="text"
                      placeholder="e.g., Apt 4B, Suite 100"
                      value={singleAddress2}
                      onChange={e => setSingleAddress2(e.target.value)}
                    />
                  </div>
                  
                  <div className="form-row three-col">
                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input
                        id="city"
                        type="text"
                        placeholder="City"
                        value={singleCity}
                        onChange={e => setSingleCity(e.target.value)}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="state">State *</label>
                      <select
                        id="state"
                        value={singleState}
                        onChange={e => setSingleState(e.target.value)}
                      >
                        <option value="">Select</option>
                        {US_STATES.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="zip">ZIP Code *</label>
                      <input
                        id="zip"
                        type="text"
                        placeholder="12345"
                        maxLength={5}
                        value={singleZip}
                        onChange={e => setSingleZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* Bulk Invite Form */
                <div className="bulk-invite-form">
                  {/* Apply to All Section */}
                  <div className="apply-to-all-section">
                    <div className="apply-to-all-header">
                      <Info size={16} />
                      <span>Apply same City, State, ZIP to all addresses</span>
                    </div>
                    <div className="apply-to-all-fields">
                      <input
                        type="text"
                        placeholder="City"
                        value={applyToAllCity}
                        onChange={e => setApplyToAllCity(e.target.value)}
                      />
                      <select
                        value={applyToAllState}
                        onChange={e => setApplyToAllState(e.target.value)}
                      >
                        <option value="">State</option>
                        {US_STATES.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="ZIP"
                        maxLength={5}
                        value={applyToAllZip}
                        onChange={e => setApplyToAllZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                      />
                      <button 
                        type="button" 
                        className="btn-apply"
                        onClick={applyToAll}
                        disabled={!applyToAllCity && !applyToAllState && !applyToAllZip}
                      >
                        Apply to All
                      </button>
                    </div>
                  </div>

                  {/* Bulk Address Table */}
                  <div className="bulk-address-table">
                    <div className="bulk-table-header">
                      <div className="col-address1">Street Address *</div>
                      <div className="col-address2">Address 2</div>
                      <div className="col-city">City *</div>
                      <div className="col-state">State *</div>
                      <div className="col-zip">ZIP *</div>
                      <div className="col-action"></div>
                    </div>
                    
                    {bulkAddresses.map((addr, index) => (
                      <div key={index} className="bulk-table-row">
                        <div className="col-address1">
                          <input
                            type="text"
                            placeholder="123 Main St"
                            value={addr.address1}
                            onChange={e => updateBulkAddress(index, 'address1', e.target.value)}
                          />
                        </div>
                        <div className="col-address2">
                          <input
                            type="text"
                            placeholder="Apt 4B"
                            value={addr.address2}
                            onChange={e => updateBulkAddress(index, 'address2', e.target.value)}
                          />
                        </div>
                        <div className="col-city">
                          <input
                            type="text"
                            placeholder="City"
                            value={addr.city}
                            onChange={e => updateBulkAddress(index, 'city', e.target.value)}
                          />
                        </div>
                        <div className="col-state">
                          <select
                            value={addr.state}
                            onChange={e => updateBulkAddress(index, 'state', e.target.value)}
                          >
                            <option value="">--</option>
                            {US_STATES.map(state => (
                              <option key={state} value={state}>{state}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-zip">
                          <input
                            type="text"
                            placeholder="12345"
                            maxLength={5}
                            value={addr.zip}
                            onChange={e => updateBulkAddress(index, 'zip', e.target.value.replace(/\D/g, '').slice(0, 5))}
                          />
                        </div>
                        <div className="col-action">
                          <button 
                            type="button"
                            className="remove-row-btn"
                            onClick={() => removeBulkRow(index)}
                            disabled={bulkAddresses.length === 1}
                            title="Remove row"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button type="button" className="btn-add-row" onClick={addBulkRow}>
                    <Plus size={16} />
                    <span>Add Another Address</span>
                  </button>
                  
                  <p className="form-hint">
                    {bulkAddresses.filter(a => a.address1.trim()).length} address(es) will be created
                  </p>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleCreateInvitations}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <Loader size={18} className="spinner" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Plus size={18} />
                    <span>
                      {createMode === 'single' 
                        ? 'Create Invitation' 
                        : `Create ${bulkAddresses.filter(a => a.address1.trim()).length || ''} Invitation(s)`}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}




// ============ Flags Section ============
function FlagsSection({ userProfile }) {
  const [flags, setFlags] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(null)

  useEffect(() => {
    loadFlags()
  }, [userProfile])

  const loadFlags = async () => {
    try {
      setLoading(true)

      const flagsQuery = query(
        collection(db, 'content_flags'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      )
      const snap = await getDocs(flagsQuery)
      const flagsData = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      }))

      // Fetch the actual discussion details for each flag
      const enrichedFlags = await Promise.all(flagsData.map(async (flag) => {
        if ((flag.contentType === 'Discussion' || flag.contentType === 'post') && flag.contentId) {
          try {
            const postDoc = await getDoc(doc(db, 'discussions', flag.contentId))
            if (postDoc.exists()) {
              const postData = postDoc.data()
              return {
                ...flag,
                postTitle: postData.title || '',
                postContent: postData.content || '',
                postAuthor: postData.authorFirstName || 'Anonymous',
                postAuthorAddress: postData.authorStreetAddress || '',
                postCategory: postData.category || '',
                postDate: postData.createdDate?.toDate() || null,
                postImageURL: postData.imageURL || '',
                postExists: true
              }
            }
          } catch (e) {
            console.error('Error fetching flagged post:', e)
          }
        }
        return { ...flag, postExists: false }
      }))

      setFlags(enrichedFlags)
    } catch (error) {
      console.error('Error loading flags:', error)
    } finally {
      setLoading(false)
    }
  }

  // Ignore: dismiss the flag but leave the post
  const handleIgnore = async (flagId) => {
    try {
      await updateDoc(doc(db, 'content_flags', flagId), {
        status: 'dismissed',
        updatedAt: serverTimestamp()
      })
      loadFlags()
    } catch (error) {
      console.error('Error ignoring flag:', error)
    }
  }

  // Delete: remove the post and mark flag as deleted
  const handleDelete = async (flag) => {
    if (!window.confirm('Are you sure you want to delete this post? This cannot be undone.')) return
    setDeleting(flag.id)
    try {
      // Delete the discussion post
      if (flag.contentId) {
        await deleteDoc(doc(db, 'discussions', flag.contentId))
        // Also delete associated comments
        const commentsQuery = query(
          collection(db, 'comments'),
          where('discussionId', '==', flag.contentId)
        )
        const commentsSnap = await getDocs(commentsQuery)
        await Promise.all(commentsSnap.docs.map(d => deleteDoc(doc(db, 'comments', d.id))))
      }
      // Mark flag as deleted
      await updateDoc(doc(db, 'content_flags', flag.id), {
        status: 'deleted',
        updatedAt: serverTimestamp()
      })
      loadFlags()
    } catch (error) {
      console.error('Error deleting flagged content:', error)
    } finally {
      setDeleting(null)
    }
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading flagged content...</p>
      </div>
    )
  }

  const unresolvedFlags = flags.filter(f => f.status === 'pending')

  return (
    <div className="section">
      <h2>Flagged Content</h2>

      {unresolvedFlags.length === 0 ? (
        <div className="empty-state">
          <Flag size={48} />
          <h3>No flagged content</h3>
          <p>All clear! No content has been flagged for review.</p>
        </div>
      ) : (
        <div className="flags-list">
          {unresolvedFlags.map(flag => (
            <div key={flag.id} className="flag-card-detailed">
              <div className="flag-badge">
                <Flag size={14} />
                <span>Flagged {flag.contentType === 'post' || flag.contentType === 'Discussion' ? 'Post' : 'Comment'}</span>
                <span className="flag-date">
                  {flag.createdAt?.toDate().toLocaleDateString()}
                </span>
              </div>

              <div className="flag-reason">
                <AlertTriangle size={14} />
                <span>Reason: {flag.reason}</span>
                {flag.reporterName && (
                  <span className="flag-reporter">— reported by {flag.reporterName}</span>
                )}
              </div>

              {flag.postExists ? (
                <div className="flagged-post-preview">
                  {flag.postCategory && (
                    <span className="flagged-post-category">{flag.postCategory}</span>
                  )}
                  <h4 className="flagged-post-title">{flag.postTitle}</h4>
                  {flag.postContent && (
                    <p className="flagged-post-body">
                      {flag.postContent.length > 300
                        ? flag.postContent.substring(0, 300) + '...'
                        : flag.postContent}
                    </p>
                  )}
                  {flag.postImageURL && (
                    <div className="flagged-post-image">
                      <img src={flag.postImageURL} alt="" />
                    </div>
                  )}
                  <div className="flagged-post-meta">
                    <User size={14} />
                    <span>{flag.postAuthor}</span>
                    {flag.postAuthorAddress && <span> • {flag.postAuthorAddress}</span>}
                    {flag.postDate && <span> • {flag.postDate.toLocaleDateString()}</span>}
                  </div>
                </div>
              ) : (
                <div className="flagged-post-preview flagged-post-deleted">
                  <p>Original post is no longer available.</p>
                </div>
              )}

              <div className="flag-actions">
                <button
                  className="btn-flag-ignore"
                  onClick={() => handleIgnore(flag.id)}
                >
                  <Check size={16} />
                  Ignore
                </button>
                <button
                  className="btn-flag-delete"
                  onClick={() => handleDelete(flag)}
                  disabled={deleting === flag.id || !flag.postExists}
                >
                  <Trash2 size={16} />
                  {deleting === flag.id ? 'Deleting...' : 'Delete Post'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ============ Announcements Section ============
// Reuses TypeSelectorModal + CreateAnnouncementModal from
// `components/AnnouncementModals` so admins see exactly the same create
// flow neighbors get on the public Announcements page (announcement OR
// poll, with end times). Edit on a card opens the same modal in edit
// mode; Delete prompts for confirmation and then `deleteDoc`s.
function AnnouncementsSection({ userProfile }) {
  const [success, setSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [announcements, setAnnouncements] = useState([])
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(true)

  // Modal state
  const [showTypeSelector, setShowTypeSelector] = useState(false)
  const [createType, setCreateType] = useState(null)        // 'announcement' | 'poll' (create mode)
  const [editingAnnouncement, setEditingAnnouncement] = useState(null) // edit mode

  // Anyone whose profile carries an admin/board role on this neighborhood
  // can manage announcements. Mirrors the gate in App.jsx that lets them
  // see the Admin tab in the first place.
  const canManage = !!(userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin)

  useEffect(() => {
    if (userProfile?.neighborhoodId) {
      loadAnnouncements()
    }
  }, [userProfile?.neighborhoodId])

  const loadAnnouncements = async () => {
    try {
      setLoadingAnnouncements(true)
      const snap = await getDocs(query(
        collection(db, 'announcements'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      ))
      // Read createdDate (iOS / public web schema) with createdAt as a
      // fallback for legacy admin-created docs that used the wrong field.
      // Surface the resolved value as `createdAt` for the card UI.
      const data = snap.docs.map(doc => {
        const d = doc.data()
        const created = d.createdDate?.toDate?.() || d.createdAt?.toDate?.() || null
        return {
          id: doc.id,
          ...d,
          createdAt: created,
        }
      })
      setAnnouncements(data.sort((a, b) => {
        const dateA = a.createdAt || new Date(0)
        const dateB = b.createdAt || new Date(0)
        return dateB - dateA
      }))
    } catch (error) {
      console.error('Error loading announcements:', error)
    } finally {
      setLoadingAnnouncements(false)
    }
  }

  const flashSuccess = (msg) => {
    setSuccessMessage(msg)
    setSuccess(true)
    setTimeout(() => setSuccess(false), 3000)
  }

  const handleTypeSelected = (type) => {
    setShowTypeSelector(false)
    setCreateType(type)
  }

  const handleCloseModal = () => {
    setCreateType(null)
    setEditingAnnouncement(null)
  }

  const handleCreated = () => {
    const wasEdit = !!editingAnnouncement
    handleCloseModal()
    flashSuccess(wasEdit ? 'Announcement updated successfully!' : 'Announcement posted successfully!')
    loadAnnouncements()
  }

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement)
  }

  const handleDelete = async (announcement) => {
    const confirmed = window.confirm(
      `Delete the announcement "${announcement.title}"? This can't be undone.`
    )
    if (!confirmed) return
    try {
      await deleteDoc(doc(db, 'announcements', announcement.id))
      flashSuccess('Announcement deleted.')
      loadAnnouncements()
    } catch (error) {
      console.error('Error deleting announcement:', error)
    }
  }

  const formatDate = (date) => {
    if (!date) return 'Unknown'
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2>Manage Announcements</h2>
        {canManage && (
          <button
            type="button"
            className="btn-add-circle"
            onClick={() => setShowTypeSelector(true)}
            title="Create announcement or poll"
          >
            <Plus size={24} />
          </button>
        )}
      </div>

      {success && (
        <div className="success-message">
          <Check size={18} />
          {successMessage || 'Announcement posted successfully!'}
        </div>
      )}

      <p className="section-description">
        Post announcements or polls for your neighborhood. Both formats appear in the Announcements tab for neighbors.
      </p>

      <h3 style={{ marginTop: '1.5rem' }}>Posted</h3>
      {loadingAnnouncements ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading announcements...</p>
        </div>
      ) : announcements.length === 0 ? (
        <div className="empty-state">
          <Megaphone size={48} />
          <h3>No announcements yet</h3>
          <p>Tap the green plus to create your first announcement or poll.</p>
        </div>
      ) : (
        <div className="content-list">
          {announcements.map(announcement => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              formatDate={formatDate}
              canManage={canManage}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isBeingEdited={editingAnnouncement?.id === announcement.id}
            />
          ))}
        </div>
      )}

      {showTypeSelector && (
        <TypeSelectorModal
          onClose={() => setShowTypeSelector(false)}
          onSelect={handleTypeSelected}
        />
      )}

      {(createType || editingAnnouncement) && (
        <CreateAnnouncementModal
          type={createType}
          existing={editingAnnouncement}
          onClose={handleCloseModal}
          onCreated={handleCreated}
          userProfile={userProfile}
        />
      )}
    </div>
  )
}

// ============ Subscription Section ============
function SubscriptionSection({ userProfile }) {
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [subscribing, setSubscribing] = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)
  const [changingPlan, setChangingPlan] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
const [clientSecret, setClientSecret] = useState(null)
const [successMessage, setSuccessMessage] = useState(null)
const [showCheckoutModal, setShowCheckoutModal] = useState(false)
const [paymentHistory, setPaymentHistory] = useState([])
const [loadingPayments, setLoadingPayments] = useState(false)

  useEffect(() => {
    // Wait until userProfile is hydrated before firing either loader.
    // Without this guard the first render fired with userProfile still
    // null, `userProfile.neighborhoodId` threw synchronously inside
    // getDoc(), and — combined with a super-admin user or a
    // canceled-subscription state that leaves neighborhoodId blank —
    // the spinner could get stuck as effects re-fired on every
    // re-render before the auth-restored userProfile arrived.
    if (!userProfile?.neighborhoodId) {
      setLoading(false)
      return
    }
    loadSubscription()
    loadPaymentHistory()
  }, [userProfile?.neighborhoodId])

  const loadSubscription = async () => {
    if (!userProfile?.neighborhoodId) {
      setLoading(false)
      return
    }
    try {
      const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId))
      if (neighborhoodDoc.exists()) {
        const data = neighborhoodDoc.data()
        const sub = data.subscription || null
        if (sub) {
          // The webhook / syncSubscriptionToFirestore writes
          // stripeSubscriptionId + stripeCustomerId at the TOP level of
          // the neighborhood doc, not nested under `subscription`.
          // Mirror them onto the local `sub` object so downstream
          // handlers (cancel / reactivate / changePlan) actually have
          // the IDs — before this, every "Cancel" click on the web
          // skipped the Stripe API call and only flipped Firestore,
          // which is how `wsgoris@gmail.com` kept getting charged
          // after a September cancellation.
          sub.stripeSubscriptionId = sub.stripeSubscriptionId || data.stripeSubscriptionId || null
          sub.stripeCustomerId = sub.stripeCustomerId || data.stripeCustomerId || null
          // Use neighborhood createdAt as fallback for missing subscription dates
          if (!sub.currentPeriodStart && data.createdAt) {
            sub.currentPeriodStart = data.createdAt
          }
          if (!sub.currentPeriodEnd && sub.trialEndsAt) {
            sub.currentPeriodEnd = sub.trialEndsAt
          }
          // If still no currentPeriodEnd, calculate next billing date from start date and plan
          if (!sub.currentPeriodEnd && sub.currentPeriodStart) {
            const startDate = sub.currentPeriodStart.toDate ? sub.currentPeriodStart.toDate() :
                              (sub.currentPeriodStart.seconds !== undefined ? new Date(sub.currentPeriodStart.seconds * 1000) :
                              new Date(sub.currentPeriodStart))
            if (!isNaN(startDate.getTime())) {
              const now = new Date()
              const nextBilling = new Date(startDate)
              const isAnnual = sub.planName && sub.planName.toLowerCase().includes('year')
              // Advance billing date until it's in the future
              while (nextBilling <= now) {
                if (isAnnual) {
                  nextBilling.setFullYear(nextBilling.getFullYear() + 1)
                } else {
                  nextBilling.setMonth(nextBilling.getMonth() + 1)
                }
              }
              sub.currentPeriodEnd = nextBilling
            }
          }
        }
        setSubscription(sub)
      }
    } catch (error) {
      console.error('Error loading subscription:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadPaymentHistory = async () => {
    if (!userProfile?.neighborhoodId) {
      setLoadingPayments(false)
      return
    }
    try {
      setLoadingPayments(true)
      const { httpsCallable } = await import('firebase/functions')
      const { functions } = await import('../utils/firebase')
      const getPaymentHistory = httpsCallable(functions, 'getPaymentHistory')
      const result = await getPaymentHistory({ neighborhoodId: userProfile.neighborhoodId })
      setPaymentHistory(result.data.payments || [])
    } catch (error) {
      console.error('Error loading payment history:', error)
    } finally {
      setLoadingPayments(false)
    }
  }

  const formatCardBrand = (brand) => {
    if (!brand) return ''
    const brands = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover' }
    return brands[brand.toLowerCase()] || brand.charAt(0).toUpperCase() + brand.slice(1)
  }

const handleSubscribe = async (plan) => {
  try {
    setSubscribing(true)
    const { httpsCallable } = await import('firebase/functions')
    const { functions, auth } = await import('../utils/firebase')
    
    if (!auth.currentUser) {
      alert('Please log in again')
      return
    }

    const priceIds = {
      monthly: 'price_1TGRanDKjuZ7t3KNCJhknoMu',
      annual: 'price_1TGRYADKjuZ7t3KNLPKW4M1u'
    }

    const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession')
    const result = await createCheckoutSession({
      priceId: priceIds[plan],
      neighborhoodId: userProfile.neighborhoodId,
      embedded: true
    })
    
    setClientSecret(result.data.clientSecret)
    setShowCheckoutModal(true)
  } catch (error) {
    console.error('Checkout error:', error)
    alert('Unable to start checkout. Please try again.')
  } finally {
    setSubscribing(false)
  }
}

  const handleChangePlan = async (newPlan) => {
  const priceIds = {
    monthly: 'price_1TGRanDKjuZ7t3KNCJhknoMu',
    annual: 'price_1TGRYADKjuZ7t3KNLPKW4M1u'
  }

  // If subscription is canceled, always go through full checkout (requires billing info)
  if (subscription?.cancelAtPeriodEnd) {
    await handleSubscribe(newPlan)
    return
  }

  // Determine if this is a downgrade (annual → monthly) or upgrade (monthly → annual)
  const currentPlan = subscription?.plan
  const isDowngrade = currentPlan === 'annual' && newPlan === 'monthly'

  const confirmMsg = newPlan === 'annual'
    ? 'Switch to Annual plan ($249.95/year)? You will save $49.45 per year. The change will take effect immediately.'
    : 'Switch to Monthly plan ($24.95/month)? Your current annual plan will continue until the end of your billing period, then switch to monthly.'

  if (!window.confirm(confirmMsg)) return

  try {
    setChangingPlan(true)

    if (subscription?.stripeSubscriptionId) {
      // Real Stripe subscription - use API
      const { httpsCallable } = await import('firebase/functions')
      const { functions } = await import('../utils/firebase')
      const updateSubscription = httpsCallable(functions, 'updateSubscription')
      const result = await updateSubscription({
        subscriptionId: subscription.stripeSubscriptionId,
        newPriceId: priceIds[newPlan]
      })

      // Show appropriate message based on upgrade/downgrade
      if (result.data?.isDowngrade) {
        alert('Your plan will switch to monthly at the end of your current billing period.')
      } else {
        alert('Plan updated successfully!')
      }
    } else {
      // Local trial - update Firestore directly
      const { updateDoc } = await import('firebase/firestore')
      await updateDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId), {
        'subscription.plan': newPlan,
        'subscription.priceId': priceIds[newPlan]
      })
      alert('Plan updated successfully!')
    }

    loadSubscription()
  } catch (error) {
    console.error('Change plan error:', error)
    alert('Unable to change plan. Please try again.')
  } finally {
    setChangingPlan(false)
  }
}

  const handleCancelSubscription = async () => {
    try {
      setCanceling(true)

      if (subscription?.stripeSubscriptionId) {
        // Real Stripe subscription - use API
        const { httpsCallable } = await import('firebase/functions')
        const { functions } = await import('../utils/firebase')
        const cancelSubscription = httpsCallable(functions, 'cancelSubscription')
        await cancelSubscription({
          subscriptionId: subscription.stripeSubscriptionId
        })
      } else {
        // Local trial - update Firestore directly
        const { updateDoc } = await import('firebase/firestore')
        await updateDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId), {
          
          'subscription.cancelAtPeriodEnd': true
        })
      }

      setSuccessMessage('Subscription canceled. You will have access until the end of your billing period.')
      setShowCancelConfirm(false)
      loadSubscription()
    } catch (error) {
      console.error('Cancel error:', error)
      alert('Unable to cancel subscription. Please try again.')
    } finally {
      setCanceling(false)
    }
  }


  const handleReactivate = async () => {
    if (!subscription?.stripeSubscriptionId) {
      // No Stripe subscription ID - just update Firestore directly
      try {
        setCanceling(true)
        const { doc, updateDoc } = await import('firebase/firestore')
        const { db } = await import('../utils/firebase')
        await updateDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId), {
          'subscription.cancelAtPeriodEnd': false
        })
        alert('Subscription reactivated!')
        loadSubscription()
      } catch (error) {
        console.error('Reactivate error:', error)
        alert('Unable to reactivate. Please try again.')
      } finally {
        setCanceling(false)
      }
      return
    }

    try {
      setCanceling(true)
      const { httpsCallable } = await import('firebase/functions')
      const { functions } = await import('../utils/firebase')

      const reactivateSubscription = httpsCallable(functions, 'reactivateSubscription')
      await reactivateSubscription({
        subscriptionId: subscription.stripeSubscriptionId
      })

      alert('Subscription reactivated!')
      loadSubscription()
    } catch (error) {
      console.error('Reactivate error:', error)
      alert('Unable to reactivate. Please try again.')
    } finally {
      setCanceling(false)
    }
  }

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true)
      const { httpsCallable } = await import('firebase/functions')
      const { functions, auth } = await import('../utils/firebase')
      
      if (!auth.currentUser) {
        alert('Please log in again')
        return
      }
      
      const createPortalSession = httpsCallable(functions, 'createPortalSession')
      const result = await createPortalSession({ returnUrl: window.location.href })
      window.location.href = result.data.url
    } catch (error) {
      console.error('Portal error:', error)
      alert('Unable to open billing portal. Please try again.')
    } finally {
      setPortalLoading(false)
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    let date
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      // Firestore Timestamp with toDate() method
      date = timestamp.toDate()
    } else if (timestamp.seconds !== undefined) {
      // Firestore Timestamp serialized as plain object (lost toDate method)
      date = new Date(timestamp.seconds * 1000)
    } else if (typeof timestamp === 'number') {
      // Unix timestamp (seconds or milliseconds)
      date = timestamp > 1e12 ? new Date(timestamp) : new Date(timestamp * 1000)
    } else {
      date = new Date(timestamp)
    }
    if (isNaN(date.getTime())) return 'N/A'
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0
    let end
    if (endDate.toDate && typeof endDate.toDate === 'function') {
      end = endDate.toDate()
    } else if (endDate.seconds !== undefined) {
      end = new Date(endDate.seconds * 1000)
    } else if (typeof endDate === 'number') {
      end = endDate > 1e12 ? new Date(endDate) : new Date(endDate * 1000)
    } else {
      end = new Date(endDate)
    }
    const now = new Date()
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24))
    return Math.max(0, diff)
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-success">Active</span>
      case 'trialing':
        return <span className="badge badge-info">Free Trial</span>
      case 'past_due':
        return <span className="badge badge-warning">Past Due</span>
      case 'canceled':
        return <span className="badge badge-danger">Canceled</span>
      default:
        return <span className="badge">Unknown</span>
    }
  }

  const getCurrentPlan = () => {
    if (!subscription?.priceId) return subscription?.plan || 'monthly'
    if (subscription.priceId.includes('MzE5diBG') || subscription.priceId.includes('CJhknoMu') || subscription.priceId.includes('monthly')) return 'monthly'
    if (subscription.priceId.includes('AjuXnAqk') || subscription.priceId.includes('LPKW4M1u') || subscription.priceId.includes('annual')) return 'annual'
    return subscription?.plan || 'monthly'
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading subscription details...</p>
      </div>
    )
  }

  if (!subscription) {
    return (
      <div className="section">
        <h2>Subscription</h2>
        <div className="subscription-empty">
          <CreditCard size={48} className="empty-icon" />
          <h3>Choose Your Plan</h3>
          <p>Select a subscription plan for your neighborhood. All plans include a 30-day free trial.</p>
          
          <div className="pricing-cards">
            <div className="pricing-card">
              <h4>Monthly</h4>
              <div className="pricing-amount">
                <span className="price">$24.95</span>
                <span className="period">/month</span>
              </div>
              <ul className="pricing-features">
                <li><Check size={16} /> 30-day free trial</li>
                <li><Check size={16} /> Unlimited members</li>
                <li><Check size={16} /> All features included</li>
                <li><Check size={16} /> Cancel anytime</li>
              </ul>
              <button 
                onClick={() => handleSubscribe('monthly')}
                disabled={subscribing}
                className="btn-primary"
              >
                {subscribing ? 'Processing...' : 'Start Free Trial'}
              </button>
            </div>

            <div className="pricing-card featured">
              <div className="popular-badge">Best Value</div>
              <h4>Annual</h4>
              <div className="pricing-amount">
                <span className="price">$249.95</span>
                <span className="period">/year</span>
              </div>
              <div className="savings">Save $49.45 (17% off)</div>
              <ul className="pricing-features">
                <li><Check size={16} /> 30-day free trial</li>
                <li><Check size={16} /> Everything in Monthly</li>
                <li><Check size={16} /> Priority support</li>
                <li><Check size={16} /> 2 months free</li>
              </ul>
              <button 
                onClick={() => handleSubscribe('annual')}
                disabled={subscribing}
                className="btn-primary"
              >
                {subscribing ? 'Processing...' : 'Start Free Trial'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const currentPlan = getCurrentPlan()
  const isTrialing = subscription.status === 'trialing'
  const trialDaysLeft = isTrialing ? getDaysRemaining(subscription.trialEndsAt || subscription.currentPeriodEnd) : 0
  const isTrialExpired = isTrialing && trialDaysLeft <= 0

  return (
    <div className="section">
      <h2>Subscription</h2>
      

      {/* Success Message */}
      {successMessage && (
        <div className="success-banner">
          <Check size={18} />
          <span>{successMessage}</span>
          <button className="close-btn-small" onClick={() => setSuccessMessage(null)}>
            <X size={16} />
          </button>
        </div>
      )}
      <div className="subscription-card">
        <div className="subscription-header">
          <div className="subscription-plan">
            <h3>{currentPlan === 'annual' ? 'Annual Plan - $249.95/year' : 'Monthly Plan - $24.95/month'}</h3>
            {!isTrialing && getStatusBadge(subscription.cancelAtPeriodEnd ? 'canceled' : subscription.status)}
          </div>
        </div>

        {isTrialing && !subscription.cancelAtPeriodEnd && (
          <div className="subscription-trial-banner">
            <AlertCircle size={18} />
            <span>
              <strong>{trialDaysLeft} days left</strong> in your free trial. 
              Your card will be charged on {formatDate(subscription.trialEndsAt || subscription.currentPeriodEnd)}.
            </span>
          </div>
        )}


        {subscription.cancelAtPeriodEnd && (
          <div className="subscription-cancel-notice">
            <AlertCircle size={18} />
            <span>
              Your subscription is canceled and will end on {formatDate(subscription.currentPeriodEnd || subscription.trialEndsAt)}.
            </span>
          </div>
        )}


        {isTrialExpired && !subscription.cancelAtPeriodEnd && (
          <div className="subscription-cancel-notice">
            <AlertCircle size={18} />
            <span>
              Your free trial expired on {formatDate(subscription.trialEndsAt || subscription.currentPeriodEnd)}. 
              Please select a plan below to continue using NeighborTopia.
            </span>
          </div>
        )}

        <div className="subscription-details">
          <div className="detail-row">
            <Calendar size={18} />
            <div>
              <span className="detail-label">Start Date</span>
              <span className="detail-value">{formatDate(subscription.currentPeriodStart || subscription.createdAt)}</span>
            </div>
          </div>
          <div className="detail-row">
            <Calendar size={18} />
            <div>
              <span className="detail-label">{isTrialing ? 'Trial Ends / First Billing' : 'Next Billing Date'}</span>
              <span className="detail-value">{formatDate(subscription.currentPeriodEnd || subscription.trialEndsAt)}</span>
            </div>
          </div>
        </div>

      </div>

      {/* Change Plan Section */}
      <div className="subscription-card change-plan-card">
        <h3>{subscription.cancelAtPeriodEnd ? 'Resubscribe' : 'Change Plan'}</h3>
        <p className="change-plan-description">
          {subscription.cancelAtPeriodEnd
            ? 'Choose a plan to continue using NeighborTopia after your current period ends.'
            : !currentPlan
              ? 'Choose a plan for when your free trial ends.'
              : currentPlan === 'monthly'
                ? 'Switch to annual billing and save $49.45 per year!'
                : 'Need more flexibility? Switch to monthly billing.'}
        </p>
        
        <div className="plan-options">
          {/* Show Monthly option if: canceled, on trial with no plan, or currently on annual */}
          {(subscription.cancelAtPeriodEnd || !currentPlan || currentPlan === 'annual') && (
            <div className="plan-option">
              <div className="plan-option-header">
                <span className="plan-name">Monthly Plan</span>
                <span className="plan-price">$24.95/mo</span>
              </div>
              <button
                onClick={() => handleChangePlan('monthly')}
                disabled={changingPlan}
                className="btn-outline"
              >
                {changingPlan ? 'Processing...' : subscription.cancelAtPeriodEnd || !currentPlan ? 'Subscribe Monthly' : 'Switch to Monthly'}
              </button>
            </div>
          )}

          {/* Show Annual option if: canceled, on trial with no plan, or currently on monthly */}
          {(subscription.cancelAtPeriodEnd || !currentPlan || currentPlan === 'monthly') && (
            <div className="plan-option">
              <div className="plan-option-header">
                <span className="plan-name">Annual Plan</span>
                <span className="plan-price">$249.95/yr</span>
                <span className="plan-savings">Save 17%</span>
              </div>
              <button
                onClick={() => handleChangePlan('annual')}
                disabled={changingPlan}
                className="btn-primary"
              >
                {changingPlan ? 'Processing...' : subscription.cancelAtPeriodEnd || !currentPlan ? 'Subscribe Annually' : 'Switch to Annual'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Subscription Section */}
      {!subscription.cancelAtPeriodEnd && (
        <div className="subscription-card cancel-section">
          <h3>Cancel Subscription</h3>
          {!showCancelConfirm ? (
            <>
              <p>If you cancel, you'll still have access until {formatDate(subscription.currentPeriodEnd || subscription.trialEndsAt)}.</p>
              <button 
                onClick={() => setShowCancelConfirm(true)}
                className="btn-danger-outline"
              >
                Cancel Subscription
              </button>
            </>
          ) : (
            <div className="cancel-confirm">
              <p className="cancel-warning">
                <AlertCircle size={18} />
                Are you sure you want to cancel? You'll lose access to all features after {formatDate(subscription.currentPeriodEnd || subscription.trialEndsAt)}.
              </p>
              <div className="cancel-actions">
                <button 
                  onClick={() => setShowCancelConfirm(false)}
                  className="btn-secondary"
                >
                  Keep Subscription
                </button>
                <button 
                  onClick={handleCancelSubscription}
                  disabled={canceling}
                  className="btn-danger"
                >
                  {canceling ? 'Canceling...' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment History Section */}
      <div className="subscription-card payment-history-section">
        <div className="payment-history-header">
          <h3>Payment History</h3>
        </div>

        {loadingPayments && paymentHistory.length === 0 && (
          <div className="payment-loading">
            <div className="spinner" />
            <p>Loading payment history...</p>
          </div>
        )}

        {!loadingPayments && paymentHistory.length === 0 && (
          <p className="payment-empty">No payment history found.</p>
        )}

        {paymentHistory.length > 0 && (
          <div className="payment-history">
            <div className="payment-table-header">
              <span>Invoice</span>
              <span>Date</span>
              <span>Amount</span>
              <span>Payment Method</span>
              <span>Status</span>
            </div>
            {paymentHistory.map((payment) => (
              <div key={payment.id} className="payment-row">
                <span className="payment-invoice">
                  {payment.invoiceNumber || '—'}
                </span>
                <span className="payment-date">
                  {new Date(payment.date * 1000).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric'
                  })}
                </span>
                <span className="payment-amount">${payment.amount.toFixed(2)}</span>
                <span className="payment-method">
                  {payment.cardBrand && payment.cardLast4
                    ? `${formatCardBrand(payment.cardBrand)} •••• ${payment.cardLast4}`
                    : '—'}
                </span>
                <span className={`payment-status ${payment.status}`}>
                  {payment.status === 'paid' ? 'Paid' : payment.status === 'refunded' ? 'Refunded' : payment.status === 'partially_refunded' ? 'Partial Refund' : payment.status === 'open' ? 'Pending' : payment.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="subscription-help">
        <p>
          Need help with your subscription? Contact us at{' '}
          <a href="mailto:info@neighbortopia.app">info@neighbortopia.app</a>
        </p>
      </div>


{/* Embedded Checkout Modal */}
{showCheckoutModal && clientSecret && (
  <div className="modal-overlay">
    <div className="modal checkout-modal" onClick={e => e.stopPropagation()}>
      <div className="modal-header">
        <h2>Complete Your Subscription</h2>
        <button className="close-btn" onClick={() => { setShowCheckoutModal(false); setClientSecret(null); }}>
          <X size={24} />
        </button>
      </div>
      <div className="modal-body checkout-body">
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{
            clientSecret,
            onComplete: () => {
              setShowCheckoutModal(false);
              setClientSecret(null);
              loadSubscription();
            }
          }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    </div>
  </div>
)}    </div>
  )
}

// ============ Manage Discussions Section (Admin / Board Member) ============
//
// Regular admins and board members see this section on the Admin tab
// (positioned above Manage Announcements, mirroring iOS + Android).
// Scoped to the user's own neighborhood — no picker — so an HOA
// admin can only ever see and moderate their own community's threads.
// Super admins have a broader cross-neighborhood surface farther
// down the sidebar (SuperAdminDiscussionsSection), but nothing stops
// them from using this one too if they want to view their own
// neighborhood specifically.
//
// Two capabilities: click a discussion to read the full thread + all
// replies in a modal (identical UX to the SuperAdmin section), or
// click the trash icon on any card to delete the discussion and every
// comment on it atomically after a confirm dialog.
function ManageDiscussionsSection({ userProfile }) {
  const [discussions, setDiscussions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Detail modal state — matches SuperAdmin section so we can eventually
  // extract a shared component if the two drift into a maintenance burden.
  const [selectedDiscussion, setSelectedDiscussion] = useState(null)
  const [detailComments, setDetailComments] = useState([])
  const [loadingComments, setLoadingComments] = useState(false)

  // Delete confirmation state — we render a small modal rather than
  // window.confirm() so the destructive action lives in-app and mentions
  // that comments will also be removed.
  const [pendingDelete, setPendingDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const neighborhoodId = userProfile?.neighborhoodId

  useEffect(() => {
    if (neighborhoodId) {
      loadDiscussions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodId])

  const loadDiscussions = async () => {
    if (!neighborhoodId) return
    try {
      setLoading(true)
      setError('')
      const q = query(
        collection(db, 'discussions'),
        where('neighborhoodId', '==', neighborhoodId)
      )
      const snap = await getDocs(q)
      const rows = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || null,
      }))
      rows.sort((a, b) => {
        const dateA = a.createdAt || new Date(0)
        const dateB = b.createdAt || new Date(0)
        return dateB - dateA
      })
      setDiscussions(rows)
    } catch (err) {
      console.error('Error loading discussions:', err)
      setError('Failed to load discussions')
    } finally {
      setLoading(false)
    }
  }

  const loadCommentsForDiscussion = async (discussionId) => {
    if (!discussionId) return
    try {
      setLoadingComments(true)
      const q = query(
        collection(db, 'comments'),
        where('discussionId', '==', discussionId)
      )
      const snap = await getDocs(q)
      const items = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdDate: d.data().createdDate?.toDate?.() || null,
      }))
      items.sort((a, b) => {
        const dateA = a.createdDate || new Date(0)
        const dateB = b.createdDate || new Date(0)
        return dateA - dateB
      })
      setDetailComments(items)
    } catch (err) {
      console.error('Error loading comments:', err)
      setDetailComments([])
    } finally {
      setLoadingComments(false)
    }
  }

  const openDiscussionDetail = (discussion) => {
    setSelectedDiscussion(discussion)
    setDetailComments([])
    loadCommentsForDiscussion(discussion.id)
  }

  const closeDiscussionDetail = () => {
    setSelectedDiscussion(null)
    setDetailComments([])
  }

  // Delete a discussion AND every comment on it. Uses a batched write so
  // orphan comments can't survive a partial failure — either the whole
  // thread disappears or nothing changes. Matches iOS's existing
  // `deleteDiscussionAndComments` behavior, which is also invoked via
  // the Flagged Content moderation path.
  const confirmDeleteDiscussion = async () => {
    if (!pendingDelete) return
    const discussionId = pendingDelete.id
    setDeleting(true)
    try {
      // Fetch every comment on this discussion so we can delete them in
      // the same batch. Firestore batches cap at 500 writes, which is
      // plenty for a single thread; if a discussion ever grows past that
      // we'd need to chunk into multiple batches (not adding that now).
      const commentsSnap = await getDocs(
        query(collection(db, 'comments'), where('discussionId', '==', discussionId))
      )

      // Delete comments individually rather than a batch — keeps the
      // failure story simple (each delete either succeeds or logs) and
      // avoids the 500-op batch cap. Discussion itself is deleted last
      // so a mid-flight failure leaves the thread visible with fewer
      // replies rather than orphan comments pointing at nothing.
      await Promise.all(commentsSnap.docs.map(d => deleteDoc(d.ref)))
      await deleteDoc(doc(db, 'discussions', discussionId))

      setPendingDelete(null)
      // Optimistically remove from the on-screen list. loadDiscussions
      // would work too, but this feels snappier and avoids a round-trip.
      setDiscussions(prev => prev.filter(d => d.id !== discussionId))
    } catch (err) {
      console.error('Error deleting discussion:', err)
      setError('Failed to delete discussion. Please try again.')
      setPendingDelete(null)
    } finally {
      setDeleting(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return 'Unknown'
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading discussions...</p>
      </div>
    )
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2>Manage Discussions</h2>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>
      )}

      {discussions.length === 0 ? (
        <div className="empty-state">
          <MessageSquare size={48} />
          <h3>No discussions yet</h3>
          <p>Discussions posted by neighbors will appear here.</p>
        </div>
      ) : (
        <div className="content-list">
          {discussions.map(discussion => {
            // Canonical schema fields, with fallbacks for any legacy
            // web-authored docs that used the older names.
            const authorDisplay = discussion.authorFirstName
              || discussion.authorName
              || 'Unknown'
            const addressSuffix = discussion.authorStreetAddress
              ? ` — ${discussion.authorStreetAddress}`
              : ''
            const replyCount = typeof discussion.messageCount === 'number'
              ? discussion.messageCount
              : (discussion.commentCount || 0)
            const upvotes = typeof discussion.upvotes === 'number'
              ? discussion.upvotes
              : (discussion.upvotes?.length || 0)
            const downvotes = typeof discussion.downvotes === 'number'
              ? discussion.downvotes
              : (discussion.downvotes?.length || 0)
            const netVotes = upvotes - downvotes

            return (
              <div
                key={discussion.id}
                className="content-card"
                onClick={() => openDiscussionDetail(discussion)}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openDiscussionDetail(discussion)
                  }
                }}
              >
                <div className="content-main">
                  <h3>{discussion.title}</h3>
                  <p className="content-preview">
                    {discussion.content?.substring(0, 150)}
                    {discussion.content?.length > 150 ? '...' : ''}
                  </p>
                  <div className="content-meta">
                    <span className="author">{authorDisplay}{addressSuffix}</span>
                    <span className="separator">•</span>
                    <span className="date">{formatDate(discussion.createdAt)}</span>
                    <span className="separator">•</span>
                    <span className="comments">
                      {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                    </span>
                  </div>
                </div>
                <div className="content-stats" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div className="vote-count">{netVotes} votes</div>
                  {/*
                    Delete affordance. `stopPropagation` prevents the card
                    click from also opening the detail modal, and setting
                    `pendingDelete` opens the confirm dialog below.
                  */}
                  <button
                    type="button"
                    aria-label="Delete discussion"
                    title="Delete discussion"
                    onClick={(e) => {
                      e.stopPropagation()
                      setPendingDelete(discussion)
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#DC2626',
                      cursor: 'pointer',
                      padding: 6,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Detail modal — same shape as SuperAdmin section */}
      {selectedDiscussion && (() => {
        const d = selectedDiscussion
        const authorDisplay = d.authorFirstName || d.authorName || 'Unknown'
        const addressSuffix = d.authorStreetAddress ? ` — ${d.authorStreetAddress}` : ''
        return (
          <div className="modal-overlay" onClick={closeDiscussionDetail}>
            <div
              className="modal"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
            >
              <div className="modal-header">
                <h2 style={{ marginRight: 12 }}>{d.title || 'Discussion'}</h2>
                <button className="close-btn" onClick={closeDiscussionDetail}>
                  <X size={24} />
                </button>
              </div>

              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                <div
                  style={{
                    padding: '4px 0 16px',
                    borderBottom: '1px solid var(--color-border)',
                    marginBottom: 16,
                  }}
                >
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                    <strong>{authorDisplay}</strong>{addressSuffix}
                    <span style={{ margin: '0 6px' }}>•</span>
                    {formatDate(d.createdAt)}
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      lineHeight: 1.5,
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {d.content || '(no content)'}
                  </div>
                </div>

                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 8 }}>
                  {loadingComments
                    ? 'Loading replies…'
                    : detailComments.length === 0
                      ? 'No replies yet'
                      : `${detailComments.length} ${detailComments.length === 1 ? 'reply' : 'replies'}`}
                </div>

                {!loadingComments && detailComments.map(c => {
                  const cAuthor = c.authorFirstName || c.authorId || 'Unknown'
                  const cAddress = c.authorStreetAddress ? ` — ${c.authorStreetAddress}` : ''
                  return (
                    <div
                      key={c.id}
                      style={{
                        padding: '10px 12px',
                        marginBottom: 8,
                        background: 'var(--color-bg-alt)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 8,
                      }}
                    >
                      <div style={{ fontSize: 12.5, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                        <strong>{cAuthor}</strong>{cAddress}
                        <span style={{ margin: '0 6px' }}>•</span>
                        {formatDate(c.createdDate)}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          lineHeight: 1.45,
                          color: 'var(--color-text-primary)',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {c.content || ''}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="modal-actions">
                <button className="btn-secondary" onClick={closeDiscussionDetail}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Delete confirm dialog */}
      {pendingDelete && (
        <div className="modal-overlay" onClick={() => !deleting && setPendingDelete(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>Delete discussion?</h2>
              <button
                className="close-btn"
                onClick={() => !deleting && setPendingDelete(null)}
                disabled={deleting}
              >
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0 }}>
                This will permanently delete
                {' '}<strong>{pendingDelete.title || 'this discussion'}</strong>{' '}
                and all of its replies. This action cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={confirmDeleteDiscussion}
                disabled={deleting}
                style={{ background: '#DC2626' }}
              >
                {deleting ? (
                  <>
                    <Loader size={18} className="spinner" />
                    <span>Deleting…</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    <span>Delete Discussion</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


// ============ Manage Categories Section (Admin / Board Member) ============
//
// Discussion categories are per-neighborhood. If a neighborhood hasn't
// been customized, everyone falls back to `DEFAULT_DISCUSSION_CATEGORIES`
// (also duplicated on iOS + Android — keep in sync). Once an admin adds
// or removes a category, the neighborhood's explicit list is stored on
// the `discussionCategories` field of the neighborhood doc and used
// everywhere the picker or filter renders.
//
// Deletion here doesn't touch existing discussions — an old post
// tagged with a removed category still shows that category on the
// card, it just can't be picked for new posts. That matches how
// forum software typically handles taxonomy edits.
export const DEFAULT_DISCUSSION_CATEGORIES = [
  'General',
  'Maintenance',
  'Events',
  'Safety',
  'Governance',
  'Marketplace',
  'Tool Sharing',
  'Complaints',
]

function ManageCategoriesSection({ userProfile }) {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [savedNote, setSavedNote] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const [isCustomized, setIsCustomized] = useState(false)

  const neighborhoodId = userProfile?.neighborhoodId

  useEffect(() => {
    if (neighborhoodId) loadCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodId])

  const loadCategories = async () => {
    if (!neighborhoodId) return
    try {
      setLoading(true)
      setError('')
      const snap = await getDoc(doc(db, 'neighborhoods', neighborhoodId))
      const data = snap.exists() ? snap.data() : null
      const stored = Array.isArray(data?.discussionCategories)
        ? data.discussionCategories
        : null
      if (stored && stored.length > 0) {
        setCategories(stored)
        setIsCustomized(true)
      } else {
        // No custom list yet — show the defaults so admins see what
        // they'll be editing away from, rather than an empty screen.
        setCategories([...DEFAULT_DISCUSSION_CATEGORIES])
        setIsCustomized(false)
      }
    } catch (err) {
      console.error('Error loading categories:', err)
      setError('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  // Central persistence helper called by every mutation (add / remove /
  // reset). Takes an explicit `next` list rather than reading state
  // because React setState is async — we'd otherwise persist stale
  // categories. Also takes the pre-mutation list so we can roll back
  // the on-screen state if the write fails.
  const persistCategories = async (next, previous) => {
    if (!neighborhoodId) return
    if (next.length === 0) {
      // Shouldn't happen after the last-category guardrail below, but
      // if it does we refuse to write an empty list and undo locally.
      setError('You need at least one category.')
      setCategories(previous)
      return
    }
    try {
      setSaving(true)
      setError('')
      await updateDoc(doc(db, 'neighborhoods', neighborhoodId), {
        discussionCategories: next,
      })
      setIsCustomized(true)
      setSavedNote('Saved.')
      // Auto-clear the "Saved." note after a moment so it doesn't
      // linger past the point where it's still useful.
      setTimeout(() => setSavedNote(''), 2000)
    } catch (err) {
      console.error('Error saving categories:', err)
      // Roll back the visible list so what the admin sees matches
      // what actually persisted.
      setCategories(previous)
      setError('Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleAdd = () => {
    const trimmed = newCategory.trim()
    if (!trimmed) return
    // Case-insensitive uniqueness check so "General" and "general" don't
    // both show up in the picker.
    const exists = categories.some(c => c.toLowerCase() === trimmed.toLowerCase())
    if (exists) {
      setError(`"${trimmed}" is already a category.`)
      return
    }
    const previous = categories
    const next = [...categories, trimmed]
    // Optimistic UI: update locally, then persist. Roll back on failure
    // happens inside persistCategories.
    setCategories(next)
    setNewCategory('')
    setError('')
    persistCategories(next, previous)
  }

  const handleRemove = (idx) => {
    // Guard against emptying the list — a picker on the discussion
    // composer always needs at least one option.
    if (categories.length <= 1) {
      setError('You need at least one category. Add another before removing this one.')
      return
    }
    const previous = categories
    const next = categories.filter((_, i) => i !== idx)
    setCategories(next)
    setError('')
    persistCategories(next, previous)
  }

  const handleResetToDefaults = () => {
    // Native confirm — the reset wipes any custom categories the admin
    // has built up, so a "there's no undo" prompt is appropriate. Bail
    // if they cancel. If the list already equals defaults, still
    // confirm (cheap) but the resulting write is a no-op anyway.
    const confirmed = window.confirm(
      'Are you sure? This will replace your current category list with the standard defaults. Existing discussions keep their original category.'
    )
    if (!confirmed) return
    const previous = categories
    const next = [...DEFAULT_DISCUSSION_CATEGORIES]
    setCategories(next)
    setError('')
    persistCategories(next, previous)
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading categories...</p>
      </div>
    )
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2>Manage Categories</h2>
      </div>

      <p style={{ color: 'var(--color-text-secondary)', marginBottom: 16, marginTop: -4 }}>
        These categories are shown when neighbors create discussions.
        Add ones specific to your community, or remove any you don't use.
        Existing discussions keep their original category even if it's
        removed here.
      </p>

      {!isCustomized && (
        <div
          style={{
            background: 'var(--color-info-bg)',
            border: '1px solid #DBEAFE',
            borderRadius: 8,
            padding: '10px 12px',
            marginBottom: 16,
            fontSize: 13,
            color: 'var(--color-info-text)',
          }}
        >
          You're viewing the default list. Any edit you make and save
          becomes this neighborhood's custom category list.
        </div>
      )}

      {error && (
        <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>
      )}
      {savedNote && (
        <div
          style={{
            color: '#059669',
            background: '#ECFDF5',
            border: '1px solid #A7F3D0',
            borderRadius: 8,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {savedNote}
        </div>
      )}

      {/* Current categories */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 20,
        }}
      >
        {categories.length === 0 ? (
          <div style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>
            No categories yet. Add one below.
          </div>
        ) : (
          categories.map((cat, idx) => (
            <div
              key={`${cat}-${idx}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                background: 'var(--color-bg-alt)',
                border: '1px solid var(--color-border)',
                borderRadius: 999,
                fontSize: 13,
                color: 'var(--color-text-primary)',
              }}
            >
              <Tag size={13} color="#64748B" />
              <span>{cat}</span>
              <button
                type="button"
                aria-label={`Remove ${cat}`}
                title={`Remove ${cat}`}
                onClick={() => handleRemove(idx)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={14} />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Add new category */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, maxWidth: 480 }}>
        <input
          type="text"
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleAdd()
            }
          }}
          placeholder="New category name..."
          data-walk="category-add-input"
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid #CBD5E1',
            borderRadius: 8,
            fontSize: 14,
          }}
        />
        <button
          type="button"
          onClick={handleAdd}
          className="btn-secondary"
          disabled={!newCategory.trim()}
        >
          <Plus size={16} />
          <span>Add</span>
        </button>
      </div>

      {/* No Save button by design — every add / remove / reset above
          persists immediately via persistCategories. This caption spells
          that out so nobody's hunting for a save action that isn't
          there. Reset stays as a secondary destructive button. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          Changes save automatically.
        </span>
        <button
          type="button"
          className="btn-secondary"
          onClick={handleResetToDefaults}
          disabled={saving}
          title="Restore the standard category list"
        >
          Reset to defaults
        </button>
      </div>
    </div>
  )
}


// ============ Super Admin Discussions Section ============
function SuperAdminDiscussionsSection() {
  const [neighborhoods, setNeighborhoods] = useState([])
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('')
  const [discussions, setDiscussions] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingDiscussions, setLoadingDiscussions] = useState(false)
  
  // Create discussion modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newContent, setNewContent] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  // Detail modal — super admins can click any discussion card to read
  // the full body plus every reply in the same modal. Read-only for now;
  // matches the iOS DiscussionDetailView data model (comments collection
  // filtered by discussionId).
  const [selectedDiscussion, setSelectedDiscussion] = useState(null)
  const [detailComments, setDetailComments] = useState([])
  const [loadingComments, setLoadingComments] = useState(false)

  useEffect(() => {
    loadNeighborhoods()
  }, [])

  useEffect(() => {
    if (selectedNeighborhood) {
      loadDiscussions()
    }
  }, [selectedNeighborhood])

const loadNeighborhoods = async () => {
    try {
      setLoading(true)
      
      const snap = await getDocs(collection(db, 'neighborhoods'))
      const neighborhoodsList = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setNeighborhoods(neighborhoodsList.sort((a, b) => a.id.localeCompare(b.id)))
      
      // Auto-select first neighborhood
      if (neighborhoodsList.length > 0) {
        setSelectedNeighborhood(neighborhoodsList[0].id)
      }
    } catch (error) {
      console.error('Error loading neighborhoods:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadDiscussions = async () => {
    try {
      setLoadingDiscussions(true)
      const discussionsQuery = query(
        collection(db, 'discussions'),
        where('neighborhoodId', '==', selectedNeighborhood)
      )
      const snap = await getDocs(discussionsQuery)
      const discussionsData = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null
      }))
      setDiscussions(discussionsData.sort((a, b) => {
        const dateA = a.createdAt || new Date(0)
        const dateB = b.createdAt || new Date(0)
        return dateB - dateA
      }))
    } catch (error) {
      console.error('Error loading discussions:', error)
    } finally {
      setLoadingDiscussions(false)
    }
  }

  // Fetch the comments (replies) for a specific discussion. Mirrors
  // `CommunityViewModel.loadComments(for:)` on iOS — same collection,
  // same filter field, same ordering — so a super admin viewing threads
  // on the web sees exactly what residents see in the apps.
  const loadCommentsForDiscussion = async (discussionId) => {
    if (!discussionId) return
    try {
      setLoadingComments(true)
      const commentsQuery = query(
        collection(db, 'comments'),
        where('discussionId', '==', discussionId)
      )
      const snap = await getDocs(commentsQuery)
      const items = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdDate: d.data().createdDate?.toDate?.() || null,
      }))
      // Oldest first so the thread reads top-to-bottom like a
      // conversation, matching the iOS/Android detail views.
      items.sort((a, b) => {
        const dateA = a.createdDate || new Date(0)
        const dateB = b.createdDate || new Date(0)
        return dateA - dateB
      })
      setDetailComments(items)
    } catch (error) {
      console.error('Error loading comments:', error)
      setDetailComments([])
    } finally {
      setLoadingComments(false)
    }
  }

  // Open a discussion in the detail modal. Kicks off the comments fetch
  // and lets the modal render its loading state in the meantime.
  const openDiscussionDetail = (discussion) => {
    setSelectedDiscussion(discussion)
    setDetailComments([])
    loadCommentsForDiscussion(discussion.id)
  }

  const closeDiscussionDetail = () => {
    setSelectedDiscussion(null)
    setDetailComments([])
  }

  const handleCreateDiscussion = async () => {
    if (!newTitle.trim() || !newContent.trim()) {
      setCreateError('Please fill in all fields')
      return
    }

    try {
      setCreating(true)
      setCreateError('')
      
      // Write the canonical schema iOS + Android agree on. Previously
      // this section wrote `authorName`, `commentCount`, and array
      // `upvotes` / `downvotes`, which mobile clients don't understand —
      // web-created discussions rendered as "Unknown / 0 replies / 0 votes"
      // in the apps. Aligning here fixes the round-trip.
      await addDoc(collection(db, 'discussions'), {
        title: newTitle.trim(),
        content: newContent.trim(),
        neighborhoodId: selectedNeighborhood,
        authorId: 'super-admin',
        authorFirstName: 'Super Admin',
        createdAt: serverTimestamp(),
        lastActivity: serverTimestamp(),
        messageCount: 0,
        upvotes: 0,
        downvotes: 0,
        votedUsers: {},
        category: 'general',
      })
      
      setNewTitle('')
      setNewContent('')
      setShowCreateModal(false)
      loadDiscussions()
    } catch (error) {
      console.error('Error creating discussion:', error)
      setCreateError('Failed to create discussion')
    } finally {
      setCreating(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return 'Unknown'
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading neighborhoods...</p>
      </div>
    )
  }

  return (
    <div className="section super-admin-section">
      <div className="section-header">
        <h2>All Discussions</h2>
        <button 
          className="btn-add-circle" 
          onClick={() => setShowCreateModal(true)}
          disabled={!selectedNeighborhood}
          title="Create Discussion"
        >
          <Plus size={24} />
        </button>
      </div>

      {/* Neighborhood Selector */}
      <div className="neighborhood-selector">
        <Globe size={18} />
        <select
          value={selectedNeighborhood}
          onChange={e => setSelectedNeighborhood(e.target.value)}
        >
          <option value="">Select Neighborhood</option>
          {neighborhoods.map(n => (
            <option key={n.id} value={n.id}>{n.name || n.id}</option>
          ))}
        </select>
      </div>

      {/* Discussions List */}
      {loadingDiscussions ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading discussions...</p>
        </div>
      ) : discussions.length === 0 ? (
        <div className="empty-state">
          <MessageSquare size={48} />
          <h3>No discussions</h3>
          <p>No discussions found in this neighborhood.</p>
        </div>
      ) : (
        <div className="content-list">
          {discussions.map(discussion => {
            // iOS and Android write to the canonical schema:
            //   authorFirstName / authorStreetAddress — author identity
            //   messageCount                          — reply count
            //   upvotes / downvotes                   — scalar counters (Int)
            // This web section previously read authorName, commentCount,
            // and upvotes/downvotes as arrays, which is why every
            // discussion created from the apps rendered as Unknown / 0
            // comments / 0 votes. Falling back to the older field names
            // keeps legacy web-created discussions working during the
            // transition — the write path further down is being aligned
            // to the canonical schema in a companion task.
            const authorDisplay = discussion.authorFirstName
              || discussion.authorName
              || 'Unknown'
            const addressSuffix = discussion.authorStreetAddress
              ? ` — ${discussion.authorStreetAddress}`
              : ''
            const replyCount = typeof discussion.messageCount === 'number'
              ? discussion.messageCount
              : (discussion.commentCount || 0)
            const upvotes = typeof discussion.upvotes === 'number'
              ? discussion.upvotes
              : (discussion.upvotes?.length || 0)
            const downvotes = typeof discussion.downvotes === 'number'
              ? discussion.downvotes
              : (discussion.downvotes?.length || 0)
            const netVotes = upvotes - downvotes

            return (
              <div
                key={discussion.id}
                className="content-card"
                onClick={() => openDiscussionDetail(discussion)}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  // Keyboard access — Enter or Space opens the same detail
                  // modal so this stays usable without a mouse.
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openDiscussionDetail(discussion)
                  }
                }}
              >
                <div className="content-main">
                  <h3>{discussion.title}</h3>
                  <p className="content-preview">
                    {discussion.content?.substring(0, 150)}
                    {discussion.content?.length > 150 ? '...' : ''}
                  </p>
                  <div className="content-meta">
                    <span className="author">{authorDisplay}{addressSuffix}</span>
                    <span className="separator">•</span>
                    <span className="date">{formatDate(discussion.createdAt)}</span>
                    <span className="separator">•</span>
                    <span className="comments">
                      {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                    </span>
                  </div>
                </div>
                <div className="content-stats">
                  <div className="vote-count">{netVotes} votes</div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Create Discussion Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Discussion</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Neighborhood</label>
                <input type="text" value={selectedNeighborhood} disabled />
              </div>
              
              {createError && <div className="form-error">{createError}</div>}
              
              <div className="form-group">
                <label htmlFor="discussion-title">Title *</label>
                <input
                  id="discussion-title"
                  type="text"
                  placeholder="Discussion title..."
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="discussion-content">Content *</label>
                <textarea
                  id="discussion-content"
                  placeholder="Write your discussion..."
                  value={newContent}
                  onChange={e => setNewContent(e.target.value)}
                  rows={6}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateDiscussion}
                disabled={creating}
              >
                {creating ? (
                  <>
                    <Loader size={18} className="spinner" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Plus size={18} />
                    <span>Create Discussion</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        Discussion Detail Modal — read-only view of the full discussion
        body plus every reply. Same data model as iOS/Android detail
        views: the top of the modal renders the discussion doc's fields,
        the list below iterates the `comments` collection filtered by
        `discussionId`. Fields default to canonical schema (authorFirstName,
        content) with graceful fallbacks so legacy/web-created docs still
        render. Modal is dismissed by overlay click, X button, or Esc via
        the native Escape keydown SwiftUI-esque browser default (no
        explicit keydown handler needed because the browser handles Esc
        on native `<dialog>` — here we mimic that via the same overlay).
      */}
      {selectedDiscussion && (() => {
        const d = selectedDiscussion
        const authorDisplay = d.authorFirstName || d.authorName || 'Unknown'
        const addressSuffix = d.authorStreetAddress ? ` — ${d.authorStreetAddress}` : ''
        return (
          <div className="modal-overlay" onClick={closeDiscussionDetail}>
            <div
              className="modal"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
            >
              <div className="modal-header">
                <h2 style={{ marginRight: 12 }}>{d.title || 'Discussion'}</h2>
                <button className="close-btn" onClick={closeDiscussionDetail}>
                  <X size={24} />
                </button>
              </div>

              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                {/* Original post */}
                <div
                  style={{
                    padding: '4px 0 16px',
                    borderBottom: '1px solid var(--color-border)',
                    marginBottom: 16,
                  }}
                >
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                    <strong>{authorDisplay}</strong>{addressSuffix}
                    <span style={{ margin: '0 6px' }}>•</span>
                    {formatDate(d.createdAt)}
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      lineHeight: 1.5,
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {d.content || '(no content)'}
                  </div>
                </div>

                {/* Replies */}
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 8 }}>
                  {loadingComments
                    ? 'Loading replies…'
                    : detailComments.length === 0
                      ? 'No replies yet'
                      : `${detailComments.length} ${detailComments.length === 1 ? 'reply' : 'replies'}`}
                </div>

                {!loadingComments && detailComments.map(c => {
                  const cAuthor = c.authorFirstName || c.authorId || 'Unknown'
                  const cAddress = c.authorStreetAddress ? ` — ${c.authorStreetAddress}` : ''
                  return (
                    <div
                      key={c.id}
                      style={{
                        padding: '10px 12px',
                        marginBottom: 8,
                        background: 'var(--color-bg-alt)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 8,
                      }}
                    >
                      <div style={{ fontSize: 12.5, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                        <strong>{cAuthor}</strong>{cAddress}
                        <span style={{ margin: '0 6px' }}>•</span>
                        {formatDate(c.createdDate)}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          lineHeight: 1.45,
                          color: 'var(--color-text-primary)',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {c.content || ''}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="modal-actions">
                <button className="btn-secondary" onClick={closeDiscussionDetail}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}


// ============ Super Admin Announcements Section ============
function SuperAdminAnnouncementsSection() {
  const [neighborhoods, setNeighborhoods] = useState([])
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('')
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false)
  
  // Create announcement modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newContent, setNewContent] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  useEffect(() => {
    loadNeighborhoods()
  }, [])

  useEffect(() => {
    if (selectedNeighborhood) {
      loadAnnouncements()
    }
  }, [selectedNeighborhood])

const loadNeighborhoods = async () => {
    try {
      setLoading(true)
      
      const snap = await getDocs(collection(db, 'neighborhoods'))
      const neighborhoodsList = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setNeighborhoods(neighborhoodsList.sort((a, b) => a.id.localeCompare(b.id)))
      
      if (neighborhoodsList.length > 0) {
        setSelectedNeighborhood(neighborhoodsList[0].id)
      }
    } catch (error) {
      console.error('Error loading neighborhoods:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadAnnouncements = async () => {
    try {
      setLoadingAnnouncements(true)
      const announcementsQuery = query(
        collection(db, 'announcements'),
        where('neighborhoodId', '==', selectedNeighborhood)
      )
      const snap = await getDocs(announcementsQuery)
      const announcementsData = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null
      }))
      setAnnouncements(announcementsData.sort((a, b) => {
        const dateA = a.createdAt || new Date(0)
        const dateB = b.createdAt || new Date(0)
        return dateB - dateA
      }))
    } catch (error) {
      console.error('Error loading announcements:', error)
    } finally {
      setLoadingAnnouncements(false)
    }
  }

  const handleCreateAnnouncement = async () => {
    if (!newTitle.trim() || !newContent.trim()) {
      setCreateError('Please fill in all fields')
      return
    }

    try {
      setCreating(true)
      setCreateError('')
      
      await addDoc(collection(db, 'announcements'), {
        title: newTitle.trim(),
        content: newContent.trim(),
        neighborhoodId: selectedNeighborhood,
        authorId: 'super-admin',
        authorName: 'Super Admin',
        createdAt: serverTimestamp(),
        isPinned: false
      })
      
      setNewTitle('')
      setNewContent('')
      setShowCreateModal(false)
      loadAnnouncements()
    } catch (error) {
      console.error('Error creating announcement:', error)
      setCreateError('Failed to create announcement')
    } finally {
      setCreating(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return 'Unknown'
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading neighborhoods...</p>
      </div>
    )
  }

  return (
    <div className="section super-admin-section">
      <div className="section-header">
        <h2>All Announcements</h2>
       <button 
          className="btn-add-circle" 
          onClick={() => setShowCreateModal(true)}
          disabled={!selectedNeighborhood}
          title="Create Announcement"
        >
          <Plus size={24} />
        </button>
      </div>

      {/* Neighborhood Selector */}
      <div className="neighborhood-selector">
        <Globe size={18} />
        <select
          value={selectedNeighborhood}
          onChange={e => setSelectedNeighborhood(e.target.value)}
        >
          <option value="">Select Neighborhood</option>
          {neighborhoods.map(n => (
            <option key={n.id} value={n.id}>{n.name || n.id}</option>
          ))}
        </select>
      </div>

      {/* Announcements List */}
      {loadingAnnouncements ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading announcements...</p>
        </div>
      ) : announcements.length === 0 ? (
        <div className="empty-state">
          <Megaphone size={48} />
          <h3>No announcements</h3>
          <p>No announcements found in this neighborhood.</p>
        </div>
      ) : (
        <div className="content-list">
          {announcements.map(announcement => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}

      {/* Create Announcement Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Announcement</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Neighborhood</label>
                <input type="text" value={selectedNeighborhood} disabled />
              </div>
              
              {createError && <div className="form-error">{createError}</div>}
              
              <div className="form-group">
                <label htmlFor="announcement-title">Title *</label>
                <input
                  id="announcement-title"
                  type="text"
                  placeholder="Announcement title..."
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="announcement-content">Content *</label>
                <textarea
                  id="announcement-content"
                  placeholder="Write your announcement..."
                  value={newContent}
                  onChange={e => setNewContent(e.target.value)}
                  rows={6}
                />
              </div>

              {/*
                Email notice — this inline modal is separate from the
                shared CreateAnnouncementModal (in components/AnnouncementModals.jsx),
                so it needs its own copy of the same wording. Matches the
                iOS `AnnouncementEmailNoticeSection` and Android
                `AnnouncementEmailNoticeCard` components — keep all three
                in sync when editing.
              */}
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  background: 'var(--color-info-bg)',
                  border: '1px solid #DBEAFE',
                  borderRadius: 10,
                  padding: '12px 14px',
                  marginTop: 4,
                }}
              >
                <Mail
                  size={20}
                  color="#2563EB"
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <div style={{ lineHeight: 1.35 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text-primary)' }}>
                    This announcement will be emailed to all active neighbors
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--color-text-secondary)', marginTop: 3 }}>
                    Neighbors receive the full announcement in their inbox as soon as you click Create Announcement.
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateAnnouncement}
                disabled={creating}
              >
                {creating ? (
                  <>
                    <Loader size={18} className="spinner" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Plus size={18} />
                    <span>Create Announcement</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


// ============ Announcement Card (expandable) ============
function AnnouncementCard({ announcement, formatDate, canManage, onEdit, onDelete, isBeingEdited }) {
  const [expanded, setExpanded] = useState(false)

  const hasPoll = announcement.poll && announcement.poll.options?.length > 0
  const totalVotes = hasPoll ? Object.keys(announcement.poll.votes || {}).length : 0

  const handleEditClick = (e) => {
    e.stopPropagation()
    onEdit?.(announcement)
  }
  const handleDeleteClick = (e) => {
    e.stopPropagation()
    onDelete?.(announcement)
  }

  return (
    <div
      className={`content-card announcement-expandable ${expanded ? 'expanded' : ''} ${isBeingEdited ? 'being-edited' : ''}`}
      onClick={() => setExpanded(!expanded)}
      style={{ cursor: 'pointer' }}
    >
      <div className="content-main">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
          <h3 style={{ margin: 0, flex: 1, minWidth: 0 }}>
            {announcement.isPinned && <span className="pin-icon">📌</span>}
            {announcement.title}
            {hasPoll && (
              <span style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                background: 'var(--color-info-border)',
                color: 'var(--color-info-text)',
                padding: '2px 8px',
                borderRadius: '4px',
                marginLeft: '8px',
                verticalAlign: 'middle'
              }}>POLL</span>
            )}
            {isBeingEdited && (
              <span className="announcement-editing-badge">Editing</span>
            )}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            {/* Inline manage actions for board members & neighborhood
                admins. We render them only when the parent passes onEdit
                / onDelete so the same component still works in the
                Super Admin "All Announcements" view (no actions). */}
            {canManage && onEdit && (
              <button
                type="button"
                className="announcement-action-btn"
                onClick={handleEditClick}
                title="Edit announcement"
              >
                <Edit size={16} />
              </button>
            )}
            {canManage && onDelete && (
              <button
                type="button"
                className="announcement-action-btn danger"
                onClick={handleDeleteClick}
                title="Delete announcement"
              >
                <Trash2 size={16} />
              </button>
            )}
            <span style={{
              fontSize: '0.75rem',
              color: 'var(--color-text-muted)'
            }}>
              {expanded ? '▲' : '▼'}
            </span>
          </div>
        </div>

        {!expanded && announcement.content && (
          <p className="content-preview" style={{ margin: '0.5rem 0 0' }}>
            {announcement.content.substring(0, 150)}
            {announcement.content.length > 150 ? '...' : ''}
          </p>
        )}

        <div className="content-meta" style={{ marginTop: '0.5rem' }}>
          <span className="author">{announcement.authorName || 'Unknown'}</span>
          <span className="separator">•</span>
          <span className="date">{formatDate(announcement.createdAt)}</span>
        </div>

        {expanded && (
          <div style={{ marginTop: '1rem', borderTop: '1px solid var(--color-border)', paddingTop: '1rem' }}>
            {announcement.content && (
              <p
                style={{ margin: '0 0 1rem', lineHeight: 1.6, color: 'var(--color-text-primary)' }}
                dangerouslySetInnerHTML={{ __html: renderAnnouncementContent(announcement.content) }}
              />
            )}

            {hasPoll && (
              <div style={{
                background: 'var(--color-bg-alt)',
                borderRadius: '8px',
                padding: '1rem',
                marginTop: announcement.content ? '0' : '0'
              }}>
                <p style={{ margin: '0 0 0.75rem', fontWeight: 600, fontSize: '0.9rem' }}>
                  Poll Results ({totalVotes} vote{totalVotes !== 1 ? 's' : ''})
                </p>
                {announcement.poll.options.map((option, index) => {
                  const voteCount = Object.values(announcement.poll.votes || {}).filter(v => v === index).length
                  const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0
                  return (
                    <div key={index} style={{ marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px' }}>
                        <span>{option}</span>
                        <span style={{ color: 'var(--color-text-secondary)' }}>{voteCount} ({percentage}%)</span>
                      </div>
                      <div style={{
                        height: '8px',
                        background: 'var(--color-bg-alt)',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${percentage}%`,
                          background: '#4d7c0f',
                          borderRadius: '4px',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ============ Super Admin Directories Section ============
function SuperAdminDirectoriesSection() {
  const [neighborhoods, setNeighborhoods] = useState([])
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('')
  const [residents, setResidents] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingResidents, setLoadingResidents] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadNeighborhoods()
  }, [])

  useEffect(() => {
    if (selectedNeighborhood) {
      loadResidents()
    }
  }, [selectedNeighborhood])

  const loadNeighborhoods = async () => {
    try {
      setLoading(true)
      const snap = await getDocs(collection(db, 'neighborhoods'))
      const neighborhoodsList = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setNeighborhoods(neighborhoodsList.sort((a, b) =>
        (a.name || a.id).localeCompare(b.name || b.id)
      ))
      
      if (neighborhoodsList.length > 0) {
        setSelectedNeighborhood(neighborhoodsList[0].id)
      }
    } catch (error) {
      console.error('Error loading neighborhoods:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadResidents = async () => {
    try {
      setLoadingResidents(true)
      const residentsQuery = query(
        collection(db, 'users'),
        where('neighborhoodId', '==', selectedNeighborhood)
      )
      const snap = await getDocs(residentsQuery)
      const residentsData = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setResidents(residentsData.sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      ))
    } catch (error) {
      console.error('Error loading residents:', error)
    } finally {
      setLoadingResidents(false)
    }
  }

  const filteredResidents = residents.filter(resident => {
    if (!searchQuery) return true
    const search = searchQuery.toLowerCase()
    return (
      resident.name?.toLowerCase().includes(search) ||
      resident.email?.toLowerCase().includes(search) ||
      resident.address?.toLowerCase().includes(search)
    )
  })

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading neighborhoods...</p>
      </div>
    )
  }

  return (
    <div className="section super-admin-section">
      <div className="section-header">
        <h2>All Directories</h2>
        <span className="resident-count">
          {filteredResidents.length} resident{filteredResidents.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Neighborhood Selector */}
      <div className="neighborhood-selector">
        <Globe size={18} />
        <select
          value={selectedNeighborhood}
          onChange={e => setSelectedNeighborhood(e.target.value)}
        >
          <option value="">Select Neighborhood</option>
          {neighborhoods.map(n => (
            <option key={n.id} value={n.id}>{n.name || n.id}</option>
          ))}
        </select>
      </div>

      {/* Search */}
      <div className="search-box">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search residents..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="clear-btn" onClick={() => setSearchQuery('')}>
            <X size={16} />
          </button>
        )}
      </div>

      {/* Residents List */}
      {loadingResidents ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading residents...</p>
        </div>
      ) : filteredResidents.length === 0 ? (
        <div className="empty-state">
          <Users size={48} />
          <h3>No residents</h3>
          <p>
            {searchQuery 
              ? 'No residents match your search' 
              : 'No residents found in this neighborhood.'}
          </p>
        </div>
      ) : (
        <div className="directory-list">
          {filteredResidents.map(resident => (
            <div key={resident.id} className="directory-card">
              <div className="resident-avatar">
                {resident.profilePhotoURL ? (
                  <img src={resident.profilePhotoURL} alt={resident.name} />
                ) : (
                  <span>{resident.name?.charAt(0).toUpperCase() || '?'}</span>
                )}
              </div>
              <div className="resident-info">
                <h3>
                  {resident.name || 'Unknown'}
                  {resident.isBoardMember && <span className="badge badge-board">Board</span>}
                  {resident.isAdmin && !resident.isBoardMember && <span className="badge badge-admin">Admin</span>}
                </h3>
                {resident.address && (
                  <p className="resident-address">
                    <MapPin size={14} />
                    {resident.address}
                  </p>
                )}
                {(resident.email || resident.phone) && (
                  <p className="resident-contacts">
                    {resident.email && (
                      <span className="resident-email">{resident.email}</span>
                    )}
                    {resident.email && resident.phone && (
                      <span className="sep" aria-hidden="true">•</span>
                    )}
                    {resident.phone && (
                      <span className="resident-phone">{formatPhoneNumber(resident.phone)}</span>
                    )}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ============ Neighborhood Profile Fields ============
// Bio, logo URL, board members and committee chairs live on the
// neighborhood doc itself. One getDoc hydrates all four; each card
// saves independently via updateDoc so admins can edit piecemeal.
function NeighborhoodProfileFields({ userProfile }) {
  const neighborhoodId = userProfile?.neighborhoodId
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Neighborhood Bio — free-form text describing the neighborhood.
  const [bio, setBio] = useState('')
  const [savingBio, setSavingBio] = useState(false)
  const [bioToast, setBioToast] = useState('')

  // Neighborhood Logo — file upload flow: pick → crop (square, 400x400
  // via the shared ImageCropperModal) → upload to Firebase Storage →
  // write the download URL onto the neighborhood doc as `logoUrl`.
  const [logoUrl, setLogoUrl] = useState('')
  const [savingLogo, setSavingLogo] = useState(false)
  const [logoToast, setLogoToast] = useState('')
  const [logoImageToCrop, setLogoImageToCrop] = useState(null)
  const [uploadingLogo, setUploadingLogo] = useState(false)

  // Board Members / Neighborhood Admins / Committee Chairs — arrays
  // of {name,title,email?,phone?}. Admin-managed lists stored on the
  // neighborhood doc as `boardMembers`, `neighborhoodAdmins`, and
  // `committeeChairs`.
  const [board, setBoard] = useState([])
  const [savingBoard, setSavingBoard] = useState(false)
  const [boardToast, setBoardToast] = useState('')
  const [admins, setAdmins] = useState([])
  const [savingAdmins, setSavingAdmins] = useState(false)
  const [adminsToast, setAdminsToast] = useState('')
  const [committee, setCommittee] = useState([])
  const [savingCommittee, setSavingCommittee] = useState(false)
  const [committeeToast, setCommitteeToast] = useState('')

  // Directory-sourced rows — auto-populated from any user in this
  // neighborhood whose `isBoardMember` / `isAdmin` flag is true.
  // Editable: changes to name / title / phone are written back to
  // the `users/{uid}` doc on Save (see `savePeople` writeback path).
  // The `original*` snapshots are frozen at hydrate time so Save can
  // diff and only push fields the admin actually changed.
  const [boardFromDirectory, setBoardFromDirectory] = useState([])
  const [boardFromDirectoryOriginal, setBoardFromDirectoryOriginal] = useState([])
  const [adminsFromDirectory, setAdminsFromDirectory] = useState([])
  const [adminsFromDirectoryOriginal, setAdminsFromDirectoryOriginal] = useState([])

  useEffect(() => {
    if (!neighborhoodId) { setLoading(false); return }
    let cancelled = false
    ;(async () => {
      try {
        const snap = await getDoc(doc(db, 'neighborhoods', neighborhoodId))
        if (cancelled) return
        if (snap.exists()) {
          const d = snap.data() || {}
          setBio(typeof d.bio === 'string' ? d.bio : '')
          setLogoUrl(typeof d.logoUrl === 'string' ? d.logoUrl : '')
          const normalizePeople = (arr) => (Array.isArray(arr) ? arr : []).map(p => ({
            name: typeof p?.name === 'string' ? p.name : '',
            title: typeof p?.title === 'string' ? p.title : '',
            email: typeof p?.email === 'string' ? p.email : '',
            phone: typeof p?.phone === 'string' ? p.phone : '',
          }))
          setBoard(normalizePeople(d.boardMembers))
          setAdmins(normalizePeople(d.neighborhoodAdmins))
          setCommittee(normalizePeople(d.committeeChairs))
        }

        // Directory-sourced auto-populated rows. Query users in the
        // neighborhood where isBoardMember / isAdmin is true and turn
        // them into read-only rows in their respective card. The
        // profile-side edits are additive — they don't feed back into
        // the user doc, so admins can't accidentally desync roles from
        // the profile UI. Both queries are single-field where clauses
        // over `users` which Firestore auto-indexes.
        const [boardSnap, adminSnap] = await Promise.all([
          getDocs(query(
            collection(db, 'users'),
            where('neighborhoodId', '==', neighborhoodId),
            where('isBoardMember', '==', true)
          )),
          getDocs(query(
            collection(db, 'users'),
            where('neighborhoodId', '==', neighborhoodId),
            where('isAdmin', '==', true)
          )),
        ])
        if (cancelled) return
        const userToRow = (u, titleField) => ({
          name: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.name || '',
          title: (u[titleField] || '').trim(),
          email: u.email || '',
          phone: u.phone || '',
          uid: u.uid || null,
          source: 'directory',
        })
        const boardRows = boardSnap.docs
          .map(d => userToRow({ uid: d.id, ...d.data() }, 'boardTitle'))
          .sort((a, b) => a.name.localeCompare(b.name))
        const adminRows = adminSnap.docs
          .map(d => userToRow({ uid: d.id, ...d.data() }, 'adminTitle'))
          .sort((a, b) => a.name.localeCompare(b.name))
        setBoardFromDirectory(boardRows)
        setBoardFromDirectoryOriginal(boardRows.map(r => ({ ...r })))
        setAdminsFromDirectory(adminRows)
        setAdminsFromDirectoryOriginal(adminRows.map(r => ({ ...r })))
      } catch (e) {
        console.error('Error loading neighborhood profile:', e)
        setError(e?.message || 'Could not load neighborhood profile.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [neighborhoodId])

  const flashToast = (setter, msg) => {
    setter(msg)
    setTimeout(() => setter(''), 2500)
  }

  const saveBio = async () => {
    if (!neighborhoodId) return
    setSavingBio(true)
    try {
      await updateDoc(doc(db, 'neighborhoods', neighborhoodId), {
        bio: (bio || '').trim(),
        updatedAt: serverTimestamp(),
      })
      flashToast(setBioToast, 'Saved')
    } catch (e) {
      console.error('Error saving bio:', e)
      setError(e?.message || 'Could not save bio.')
    } finally {
      setSavingBio(false)
    }
  }

  // Kicks off the file → cropper flow. Called by the "Upload logo" /
  // "Change logo" button below. The cropper produces a 400x400 JPEG
  // Blob which we then upload in `handleCroppedLogo`.
  const handleLogoFilePick = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setLogoImageToCrop(reader.result)
    reader.readAsDataURL(file)
    // Reset so picking the same file twice still fires onChange.
    e.target.value = ''
  }

  const handleCroppedLogo = async (blob) => {
    setLogoImageToCrop(null)
    if (!blob || !neighborhoodId) return
    setUploadingLogo(true)
    setError('')
    try {
      const storageRef = ref(
        storage,
        `neighborhood_logos/${neighborhoodId}_${Date.now()}.jpg`
      )
      await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' })
      const downloadURL = await getDownloadURL(storageRef)
      await updateDoc(doc(db, 'neighborhoods', neighborhoodId), {
        logoUrl: downloadURL,
        updatedAt: serverTimestamp(),
      })
      setLogoUrl(downloadURL)
      flashToast(setLogoToast, 'Logo updated')
    } catch (e) {
      console.error('Error uploading logo:', e)
      setError(e?.message || 'Could not upload logo.')
    } finally {
      setUploadingLogo(false)
    }
  }

  const removeLogo = async () => {
    if (!neighborhoodId) return
    if (!window.confirm('Remove the neighborhood logo?')) return
    setSavingLogo(true)
    try {
      await updateDoc(doc(db, 'neighborhoods', neighborhoodId), {
        logoUrl: '',
        updatedAt: serverTimestamp(),
      })
      setLogoUrl('')
      flashToast(setLogoToast, 'Logo removed')
    } catch (e) {
      console.error('Error removing logo:', e)
      setError(e?.message || 'Could not remove logo.')
    } finally {
      setSavingLogo(false)
    }
  }

  // Shared normalize/save for board + committee — drops rows with no
  // name AND no title, trims all string fields, drops incomplete phone
  // numbers (must be empty or exactly 10 digits) so we never persist
  // partial phones like "402-30".
  const normalizePeople = (rows) => rows
    .map(p => {
      const rawDigits = (p.phone || '').replace(/\D/g, '')
      return {
        name: (p.name || '').trim(),
        title: (p.title || '').trim(),
        email: (p.email || '').trim(),
        phone: rawDigits.length === 10 ? rawDigits : '',
      }
    })
    .filter(p => p.name || p.title)

  const savePeople = async (field, rows, setSaving, setToast, options = {}) => {
    if (!neighborhoodId) return
    // Optional directory writeback: if `directoryRows` + `titleField` are
    // supplied, iterate the directory-sourced rows and push their edits
    // back to the underlying `users/{uid}` docs. This is the "edits sync
    // back to Directory" behavior — only fields the admin actually
    // changed relative to the original snapshot are written.
    const { directoryRows, originalDirectoryRows, titleField } = options
    setSaving(true)
    try {
      // Manual rows saved on the neighborhood doc.
      await updateDoc(doc(db, 'neighborhoods', neighborhoodId), {
        [field]: normalizePeople(rows),
        updatedAt: serverTimestamp(),
      })

      // Directory writeback — only the Title field is editable here,
      // so that's the only thing that can differ from the original
      // snapshot. Writes to boardTitle / adminTitle on `users/{uid}`.
      if (Array.isArray(directoryRows) && directoryRows.length > 0 && titleField) {
        const originals = Array.isArray(originalDirectoryRows) ? originalDirectoryRows : []
        const originalsByUid = new Map(originals.map(r => [r.uid, r]))
        await Promise.all(directoryRows.map(async (row) => {
          if (!row.uid) return
          const orig = originalsByUid.get(row.uid) || {}
          const newTitle = (row.title || '').trim()
          const origTitle = (orig.title || '').trim()
          if (newTitle !== origTitle) {
            await updateDoc(doc(db, 'users', row.uid), {
              [titleField]: newTitle,
              updatedAt: serverTimestamp(),
            })
          }
        }))
      }

      flashToast(setToast, 'Saved')
    } catch (e) {
      console.error(`Error saving ${field}:`, e)
      setError(e?.message || `Could not save ${field}.`)
    } finally {
      setSaving(false)
    }
  }

  const looksLikeUrl = (s) => /^https?:\/\/\S+\.\S+/i.test((s || '').trim())

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading neighborhood profile...</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {error && (
        <div className="error-banner" style={{ marginBottom: 8 }}>{error}</div>
      )}

      {/* Bio + Logo sit side by side on wider viewports so the top of
          the Neighborhood Profile isn't a tall single-column stack.
          The wrapper collapses to one column below 900px. */}
      <div className="np-two-col">
      {/* Bio — autosaves on blur. The old "Save bio" button was
          removed per the "just save when the user clicks out" spec.
          A tiny status chip appears on the right while saving / just
          saved so admins get a visible confirmation. */}
      <div className="form-card">
        <h2>Neighborhood Bio</h2>
        <p className="muted" style={{ marginTop: -4, marginBottom: 12 }}>
          A short description of your neighborhood shown to residents.
          Changes save automatically.
        </p>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          onBlur={saveBio}
          rows={6}
          placeholder="A short description of your neighborhood — history, character, what makes it special."
          style={{ width: '100%' }}
          data-walk="np-bio"
        />
        <AutosaveStatus saving={savingBio} toast={bioToast} />
      </div>

      {/* Logo — file upload → ImageCropperModal → Firebase Storage,
          then the download URL is written back to the neighborhood doc.
          The cropper produces a square 400x400 JPEG so the logo lands
          at a predictable, cheap-to-cache size. */}
      <div className="form-card">
        <h2>Neighborhood Logo</h2>
        <p className="muted" style={{ marginTop: -4, marginBottom: 12 }}>
          Displayed alongside the neighborhood name in the app.
          Upload a square image; you can crop after selecting.
        </p>

        {/* Current logo preview (or empty placeholder). Logo is a fixed
            96×96 square and the two stacked buttons are each 44px tall
            with an 8px gap so the right column also totals 96px — the
            two sides line up exactly. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
          <div
            style={{
              width: 96,
              height: 96,
              borderRadius: 12,
              border: '1px solid var(--border, var(--color-border))',
              background: 'var(--color-bg-card)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Neighborhood logo"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
            ) : (
              <span className="muted" style={{ fontSize: 12 }}>No logo</span>
            )}
          </div>

          {/* Buttons stack — each 44px → 44 + 8 gap + 44 = 96, matching
              the logo preview height exactly. Explicit height + padding
              override the global `button { padding }` so the two always
              line up. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label
              className="btn-outline"
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 44,
                padding: '0 16px',
                margin: 0,
                boxSizing: 'border-box',
              }}
            >
              {uploadingLogo ? 'Uploading…' : (logoUrl ? 'Change logo' : 'Upload logo')}
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoFilePick}
                disabled={uploadingLogo}
                style={{ display: 'none' }}
              />
            </label>
            {logoUrl && (
              <button
                type="button"
                className="btn-outline danger"
                onClick={removeLogo}
                disabled={uploadingLogo || savingLogo}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 44,
                  padding: '0 16px',
                  boxSizing: 'border-box',
                }}
              >
                Remove logo
              </button>
            )}
          </div>
        </div>

        {logoToast && <span className="success-banner">{logoToast}</span>}
      </div>

      {/* Cropper modal, rendered when the user picks a file. Produces a
          400x400 JPEG Blob via canvas — see ImageCropperModal.jsx. */}
      {logoImageToCrop && (
        <ImageCropperModal
          imageSrc={logoImageToCrop}
          onCancel={() => setLogoImageToCrop(null)}
          onCropDone={handleCroppedLogo}
        />
      )}
      </div>

      {/* Board Members — directory rows (users flagged isBoardMember)
          are auto-included and read-only; additional entries can be
          added manually and are saved to `boardMembers`. */}
      <PeopleEditorCard
        heading="Board Members"
        description="Any user marked as Board Member is included automatically."
        directoryRows={boardFromDirectory}
        setDirectoryRows={setBoardFromDirectory}
        rows={board}
        setRows={setBoard}
        onSave={() => savePeople('boardMembers', board, setSavingBoard, setBoardToast, {
          directoryRows: boardFromDirectory,
          originalDirectoryRows: boardFromDirectoryOriginal,
          titleField: 'boardTitle',
        })}
        saving={savingBoard}
        toast={boardToast}
        directoryOnly
      />

      {/* Neighborhood Admins — same pattern as Board Members but
          sourced from `isAdmin`. Directory edits write back to
          `users/{uid}` with title going into `adminTitle`. */}
      <PeopleEditorCard
        heading="Neighborhood Admins"
        description="Any user marked as Neighborhood Admin is included automatically."
        directoryRows={adminsFromDirectory}
        setDirectoryRows={setAdminsFromDirectory}
        rows={admins}
        setRows={setAdmins}
        onSave={() => savePeople('neighborhoodAdmins', admins, setSavingAdmins, setAdminsToast, {
          directoryRows: adminsFromDirectory,
          originalDirectoryRows: adminsFromDirectoryOriginal,
          titleField: 'adminTitle',
        })}
        saving={savingAdmins}
        toast={adminsToast}
        directoryOnly
      />

      {/* Committee Chairs section removed per product request — the
          `committeeChairs` field is still preserved on existing
          neighborhood docs; only the admin editor is hidden. */}
    </div>
  )
}

// Shared editor for Board Members and Committee Chairs — same schema
// and interactions (add/remove/reorder), so factor once.
// Progressive phone-typing formatter that outputs XXX-XXX-XXXX (all
// dashes, no parens). Matches the format the user asked for on this
// admin surface — distinct from the app-wide (XXX) XXX-XXXX display.
function formatPhoneDashes(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10)
  if (digits.length === 0) return ''
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

function PeopleEditorCard({ heading, description, rows, setRows, onSave, saving, toast, directoryRows = [], setDirectoryRows, directoryOnly = false }) {
  const updateDirectoryRow = (idx, field, value) => {
    if (!setDirectoryRows) return
    setDirectoryRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }
  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx))
  const addRow = () => setRows(prev => [...prev, { name: '', title: '', email: '', phone: '' }])

  return (
    <div className="form-card">
      <h2>{heading}</h2>
      <p className="muted" style={{ marginTop: -4, marginBottom: 12 }}>{description}</p>

      {/* Directory-sourced rows (isBoardMember / isAdmin). Only Title
          is editable here — that's the field admins actually change
          from this surface, and it writes back to boardTitle /
          adminTitle on the user doc. Name / email / phone are shown
          read-only to give context but are managed by the resident
          themselves via Profile (or by an admin via Manage Households). */}
      {directoryRows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          {directoryRows.map((row, idx) => (
            <div
              key={`dir-${row.uid || idx}`}
              style={{
                display: 'grid',
                // Fixed 130px action column so directory rows (with
                // the "From Directory" badge) and manual rows (with
                // the trash button) share the same 4 field widths.
                // In `directoryOnly` mode there are no manual rows and
                // no badge, so the 4 field columns fill the width.
                gridTemplateColumns: directoryOnly
                  ? '1fr 1fr 1fr 1fr'
                  : '1fr 1fr 1fr 1fr 130px',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <input
                type="text"
                value={row.name}
                disabled
                title="Managed by the resident from Profile."
                style={{ opacity: 0.7 }}
              />
              <input
                type="text"
                placeholder="Title (e.g. President)"
                value={row.title}
                onChange={(e) => updateDirectoryRow(idx, 'title', e.target.value)}
                onBlur={onSave}
              />
              <input
                type="email"
                value={row.email}
                disabled
                title="Managed by the resident from Profile."
                style={{ opacity: 0.7 }}
              />
              <input
                type="tel"
                value={formatPhoneDashes(row.phone)}
                disabled
                title="Managed by the resident from Profile."
                style={{ opacity: 0.7 }}
              />
              {!directoryOnly && (
                <span
                  title="Sourced from the Directory. Title edits sync back on Save."
                  style={{
                    fontSize: 11,
                    padding: '2px 8px',
                    borderRadius: 999,
                    background: 'var(--color-primary-light, #dcfce7)',
                    color: 'var(--color-primary-dark, #166534)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  From Directory
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {!directoryOnly && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((row, idx) => (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <input
              type="text"
              placeholder="Name"
              value={row.name}
              onChange={(e) => updateRow(idx, 'name', e.target.value)}
              onBlur={onSave}
            />
            <input
              type="text"
              placeholder="Title (e.g. President)"
              value={row.title}
              onChange={(e) => updateRow(idx, 'title', e.target.value)}
              onBlur={onSave}
            />
            <input
              type="email"
              placeholder="Email (optional)"
              value={row.email}
              onChange={(e) => updateRow(idx, 'email', e.target.value)}
              onBlur={onSave}
            />
            {(() => {
              const digits = String(row.phone || '').replace(/\D/g, '')
              const isPartial = digits.length > 0 && digits.length !== 10
              return (
                <input
                  type="tel"
                  placeholder="555-555-5555"
                  value={formatPhoneDashes(row.phone)}
                  onChange={(e) => updateRow(idx, 'phone', e.target.value)}
                  onBlur={onSave}
                  title={isPartial
                    ? 'Phone must be exactly 10 digits to save.'
                    : ''}
                  style={isPartial
                    ? { borderColor: 'var(--color-error)', boxShadow: '0 0 0 1px var(--color-error)' }
                    : undefined}
                />
              )
            })()}
            <button
              type="button"
              className="btn-outline small danger"
              title="Remove"
              onClick={() => { removeRow(idx); setTimeout(onSave, 0) }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      )}

      {/* Modern dashed "+ Add person" affordance under the rows.
          Mirrors the "+ Add line" pattern from the invoicing-style
          line-items UI — feels like a placeholder inviting a click,
          rather than a filled button. Hidden in `directoryOnly` mode
          where only Directory-flagged users make the list. */}
      {!directoryOnly && (
        <button
          type="button"
          onClick={addRow}
          style={{
            marginTop: rows.length > 0 ? 10 : 0,
            padding: '10px 14px',
            border: '1px dashed var(--border, #cbd5e1)',
            borderRadius: 8,
            background: 'transparent',
            color: 'var(--color-text-secondary, #64748b)',
            fontSize: 14,
            cursor: 'pointer',
            textAlign: 'left',
            width: 'fit-content',
            minWidth: 140,
          }}
        >
          + Add person
        </button>
      )}

      {/* No Save button — every input autosaves on blur (see onBlur
          handlers on each field above). AutosaveStatus surfaces a
          brief "Saving…" / "Saved" chip so admins get a confirmation. */}
      <AutosaveStatus saving={saving} toast={toast} />
    </div>
  )
}

// Compact right-aligned "Saving… / Saved" chip. Replaces the old
// explicit Save button now that fields autosave on blur.
function AutosaveStatus({ saving, toast }) {
  if (!saving && !toast) return null
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: 8,
      fontSize: 12,
      color: 'var(--color-text-secondary)',
    }}>
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        background: saving
          ? 'var(--color-bg-alt, #f2f4f2)'
          : 'var(--color-primary-bg, rgba(124,182,95,0.10))',
        color: saving
          ? 'var(--color-text-secondary)'
          : 'var(--color-primary-dark)',
      }}>
        {saving ? 'Saving…' : `✓ ${toast || 'Saved'}`}
      </span>
    </div>
  )
}

// ============ Resources Admin Section ============
// Add this component to AdminPage.jsx (before the final export)

function NeighborhoodProfileSection({ userProfile }) {
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  
  // Form state
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [pdfFile, setPdfFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (userProfile?.neighborhoodId) {
      loadResources()
    }
  }, [userProfile?.neighborhoodId])

  const loadResources = async () => {
    try {
      setLoading(true)
      const resourcesQuery = query(
        collection(db, 'resources'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        orderBy('createdAt', 'desc')
      )
      
      const snapshot = await getDocs(resourcesQuery)
      const resourcesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }))
      
      setResources(resourcesList)
    } catch (error) {
      console.error('Error loading resources:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateResource = async () => {
    if (!title.trim()) {
      setError('Please enter a title')
      return
    }
    
    if (!content.trim() && !pdfFile) {
      setError('Please enter content or upload a PDF')
      return
    }

    try {
      setUploading(true)
      setError('')
      
      let pdfUrl = null
      
      // Upload PDF if provided.
      // Explicitly set contentType so Firebase Storage serves the file with
      // the correct MIME type — otherwise browsers receive
      // `application/octet-stream` and force a download instead of rendering
      // the PDF inline in our viewer iframe/object.
      if (pdfFile) {
        const storageRef = ref(storage, `resources/${userProfile.neighborhoodId}/${Date.now()}_${pdfFile.name}`)
        await uploadBytes(storageRef, pdfFile, {
          contentType: pdfFile.type || 'application/pdf',
          contentDisposition: `inline; filename="${pdfFile.name}"`,
        })
        pdfUrl = await getDownloadURL(storageRef)
      }
      
      await addDoc(collection(db, 'resources'), {
        title: title.trim(),
        content: content.trim(),
        pdfUrl,
        neighborhoodId: userProfile.neighborhoodId,
        createdBy: userProfile.uid,
        createdByName: userProfile.name,
        createdAt: serverTimestamp()
      })
      
      // Reset form
      setTitle('')
      setContent('')
      setPdfFile(null)
      setShowCreateModal(false)
      loadResources()
    } catch (error) {
      console.error('Error creating resource:', error)
      setError('Failed to create resource')
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return
    
    try {
      await deleteDoc(doc(db, 'resources', resourceId))
      loadResources()
    } catch (error) {
      console.error('Error deleting resource:', error)
    }
  }

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const resetModal = () => {
    setTitle('')
    setContent('')
    setPdfFile(null)
    setError('')
    setShowCreateModal(false)
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading resources...</p>
      </div>
    )
  }

  return (
    <div className="section resources-admin-section">
      {/* Bio / Logo / Board / Committee editors — persisted on the
          neighborhood doc itself, hydrated in one getDoc. */}
      <NeighborhoodProfileFields userProfile={userProfile} />

      {/* Quick Resources — short contact-style cards (title / description
          / URL / phone) that appear in a sidebar on the resident-facing
          Resources page. Managed independently from the documents list
          below, but rendered together for admin convenience. */}
      <QuickResourcesAdmin userProfile={userProfile} />

      <div className="section-header" style={{ marginTop: 32 }}>
        <h2>Manage Resources</h2>
        <button
          className="btn-add-circle"
          onClick={() => setShowCreateModal(true)}
          title="Add Resource"
        >
          <Plus size={24} />
        </button>
      </div>

      <p className="section-description">
        Add community documents like covenants, by-laws, HOA rules, and other important resources for residents to access.
      </p>

      {/* Resources List */}
      {resources.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <h3>No resources yet</h3>
          <p>Click the + button to add your first community resource.</p>
        </div>
      ) : (
        <div className="resources-admin-list">
          {resources.map(resource => (
            <div key={resource.id} className="resource-admin-card">
              <div className="resource-admin-icon">
                <FileText size={20} />
              </div>
              <div className="resource-admin-info">
                <h4>{resource.title}</h4>
                <div className="resource-admin-meta">
                  <span>{formatDate(resource.createdAt)}</span>
                  {resource.pdfUrl && <span className="pdf-badge">PDF</span>}
                </div>
              </div>
              <div className="resource-admin-actions">
                {resource.pdfUrl && (
                  <a 
                    href={resource.pdfUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-icon"
                    title="View PDF"
                  >
                    <ExternalLink size={18} />
                  </a>
                )}
                <button 
                  className="btn-icon delete"
                  onClick={() => handleDeleteResource(resource.id)}
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Resource Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={resetModal}>
          <div className="modal create-resource-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Resource</h2>
              <button className="close-btn" onClick={resetModal}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}
              
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g., Community Covenants, HOA By-Laws"
                />
              </div>
              
              <div className="form-group">
                <label>Content</label>
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder="Enter the document content or a summary..."
                  rows={8}
                />
              </div>
              
              <div className="form-group">
                <label>PDF Document (Optional)</label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={e => setPdfFile(e.target.files[0])}
                    id="pdf-upload"
                    className="file-input-hidden"
                  />
                  <label htmlFor="pdf-upload" className="file-upload-label">
                    <Upload size={20} />
                    <span>{pdfFile ? pdfFile.name : 'Choose PDF file'}</span>
                  </label>
                  {pdfFile && (
                    <button 
                      type="button" 
                      className="btn-remove-file"
                      onClick={() => setPdfFile(null)}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-cancel" onClick={resetModal}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleCreateResource}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader size={18} className="spinner-icon" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Plus size={18} />
                    Add Resource
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============ Quick Resources (Admin) ============
// Short contact-info cards (title / description / URL / phone) shown in
// a sidebar on the resident Resources page. Kept in its own component so
// the CRUD state doesn't tangle with the document-list state above.
function QuickResourcesAdmin({ userProfile }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState({ open: false, editing: null }) // editing = item or null
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [url, setUrl] = useState('')
  const [phone, setPhone] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const neighborhoodId = userProfile?.neighborhoodId

  useEffect(() => {
    if (neighborhoodId) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodId])

  const load = async () => {
    try {
      setLoading(true)
      // Sort client-side to avoid needing a composite (neighborhoodId +
      // createdAt) Firestore index. Quick resources per neighborhood are
      // a small list so in-memory sort is cheap.
      const snap = await getDocs(query(
        collection(db, 'quickResources'),
        where('neighborhoodId', '==', neighborhoodId)
      ))
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      rows.sort((a, b) => {
        const at = a.createdAt?.toMillis?.() || 0
        const bt = b.createdAt?.toMillis?.() || 0
        return bt - at
      })
      setItems(rows)
    } catch (e) {
      console.error('Error loading quick resources:', e)
    } finally {
      setLoading(false)
    }
  }

  const openCreate = () => {
    setTitle('')
    setDescription('')
    setUrl('')
    setPhone('')
    setError('')
    setModal({ open: true, editing: null })
  }

  const openEdit = (item) => {
    setTitle(item.title || '')
    setDescription(item.description || '')
    setUrl(item.url || '')
    setPhone(item.phone || '')
    setError('')
    setModal({ open: true, editing: item })
  }

  const close = () => setModal({ open: false, editing: null })

  const handleSave = async () => {
    const t = title.trim()
    const d = description.trim()
    const u = url.trim()
    const p = phone.trim()

    if (!t) {
      setError('Title is required.')
      return
    }
    // Must have at least one contact method — URL or phone.
    if (!u && !p) {
      setError('Add either a URL or a phone number (or both).')
      return
    }

    setSaving(true)
    setError('')
    try {
      const payload = {
        title: t,
        description: d,
        // Persist empty strings rather than nulls so the docs stay
        // shape-consistent across creates and edits.
        url: u,
        phone: p,
        neighborhoodId,
        updatedAt: serverTimestamp(),
      }
      if (modal.editing) {
        await updateDoc(doc(db, 'quickResources', modal.editing.id), payload)
      } else {
        await addDoc(collection(db, 'quickResources'), {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: userProfile.uid,
          createdByName: userProfile.name || '',
        })
      }
      close()
      load()
    } catch (e) {
      console.error('Error saving quick resource:', e)
      setError('Could not save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return
    try {
      await deleteDoc(doc(db, 'quickResources', item.id))
      load()
    } catch (e) {
      console.error('Error deleting quick resource:', e)
    }
  }

  // Same formatting the resident-facing sidebar uses — kept locally so
  // the admin list matches. Falls back to whatever the admin typed if
  // the digits don't fit a US 10- or 11-digit pattern.
  const formatPhone = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '')
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`
    }
    return raw
  }

  return (
    <div style={{ marginTop: 32 }}>
      <div className="section-header">
        <h2>Quick Resources</h2>
        <button
          className="btn-add-circle"
          onClick={openCreate}
          title="Add Quick Resource"
        >
          <Plus size={24} />
        </button>
      </div>
      <p className="section-description">
        Short contact-style cards (property manager, trash pickup, emergency
        line, community pool code, etc.) shown in a sidebar on the Resources
        page. Add at least a title plus a URL or phone number.
      </p>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading…</p>
        </div>
      ) : items.length === 0 ? (
        <div
          className="empty-state"
          style={{ padding: 16, background: 'var(--color-bg-alt)', borderRadius: 8, marginBottom: 8 }}
        >
          <Zap size={28} />
          <h3 style={{ margin: '8px 0 4px 0', fontSize: 14 }}>No quick resources yet</h3>
          <p style={{ fontSize: 13, margin: 0 }}>
            Click the + button to add one.
          </p>
        </div>
      ) : (
        <div className="resources-admin-list">
          {items.map(item => (
            <div key={item.id} className="resource-admin-card">
              <div className="resource-admin-icon">
                <Zap size={20} />
              </div>
              <div className="resource-admin-info">
                <h4>{item.title}</h4>
                <div className="resource-admin-meta" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {item.url && (
                    <span title={item.url} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <LinkIcon size={12} /> URL
                    </span>
                  )}
                  {item.phone && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Phone size={12} /> {formatPhone(item.phone)}
                    </span>
                  )}
                  {item.description && (
                    <span
                      style={{
                        color: 'var(--color-text-secondary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 280,
                      }}
                      title={item.description}
                    >
                      · {item.description}
                    </span>
                  )}
                </div>
              </div>
              <div className="resource-admin-actions">
                <button
                  className="btn-icon"
                  onClick={() => openEdit(item)}
                  title="Edit"
                >
                  <Edit size={18} />
                </button>
                <button
                  className="btn-icon delete"
                  onClick={() => handleDelete(item)}
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal.open && (
        <div className="modal-overlay" onClick={close}>
          <div className="modal create-resource-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modal.editing ? 'Edit Quick Resource' : 'Add Quick Resource'}</h2>
              <button className="close-btn" onClick={close}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g., Property Manager, Trash Pickup, Pool Code"
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <input
                  type="text"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Short helpful note (e.g. 'M-F 9-5', 'Every Tuesday morning')"
                />
              </div>
              <div className="form-group">
                <label>URL (optional)</label>
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://…"
                />
              </div>
              <div className="form-group">
                <label>Phone (optional)</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="555-555-5555"
                />
              </div>
              <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                At least one of URL or Phone is required.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={close}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <><Loader size={18} className="spinner-icon" /> Saving…</>
                ) : (
                  <><Check size={18} /> {modal.editing ? 'Save changes' : 'Add resource'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


// ============================================================
// Footer Links admin section
// ============================================================
// Per-neighborhood external links surfaced in the site footer once a
// resident signs in. Persists to `neighborhoods/{id}.footerLinks` as an
// ordered array of { label, url }. MainLayout subscribes to that same
// field via onSnapshot so residents see edits within a second.
function FooterLinksSection({ userProfile }) {
  const [links, setLinks] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState('')
  const [neighborhoodName, setNeighborhoodName] = useState('')

  const neighborhoodId = userProfile?.neighborhoodId

  // Suggested defaults for a brand-new neighborhood — click "Populate
  // defaults" and each row shows up empty of a URL for the admin to
  // paste in. Labels match the pattern used on lindenpark.org and
  // similar HOA sites so it feels familiar.
  // Quick Resources isn't in this list — it's rendered automatically
  // by MainLayout as an internal link to /resources (the page that
  // already surfaces the Quick Resources cards). Admins only manage
  // the *external* neighborhood links here.
  const suggestedDefaults = (name) => ([
    { label: name ? `About ${name}` : 'About', url: '' },
    { label: 'HOA Board', url: '' },
    { label: 'Community Links', url: '' },
    { label: 'By-Laws & Covenants', url: '' },
  ])

  useEffect(() => {
    if (!neighborhoodId) {
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const snap = await getDoc(doc(db, 'neighborhoods', neighborhoodId))
        if (cancelled) return
        if (snap.exists()) {
          const data = snap.data() || {}
          setNeighborhoodName(data.name || '')
          const raw = Array.isArray(data.footerLinks) ? data.footerLinks : []
          setLinks(raw.map(l => ({
            label: typeof l?.label === 'string' ? l.label : '',
            url: typeof l?.url === 'string' ? l.url : '',
          })))
        }
      } catch (e) {
        console.error('Error loading footer links:', e)
        setError(e?.message || 'Could not load footer links.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [neighborhoodId])

  const updateRow = (idx, field, value) => {
    setLinks(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row))
  }
  const removeRow = (idx) => {
    setLinks(prev => prev.filter((_, i) => i !== idx))
  }
  const addRow = () => {
    setLinks(prev => [...prev, { label: '', url: '' }])
  }
  const moveRow = (idx, delta) => {
    setLinks(prev => {
      const next = [...prev]
      const target = idx + delta
      if (target < 0 || target >= next.length) return prev
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next
    })
  }
  const applyDefaults = () => {
    setLinks(prev => {
      // Merge — don't blow away existing labels/URLs the admin already set.
      const existingByLabel = new Map(prev.map(l => [l.label.toLowerCase(), l]))
      const merged = suggestedDefaults(neighborhoodName).map(def => {
        const hit = existingByLabel.get(def.label.toLowerCase())
        return hit || def
      })
      // Preserve any user-added rows that aren't in the defaults list.
      const defaultLabelsLc = new Set(
        suggestedDefaults(neighborhoodName).map(d => d.label.toLowerCase())
      )
      const extras = prev.filter(l => !defaultLabelsLc.has(l.label.toLowerCase()))
      return [...merged, ...extras]
    })
  }

  const save = async () => {
    if (!neighborhoodId) return
    setSaving(true)
    setError('')
    try {
      // Normalize: trim, drop rows with empty URL (labels alone are
      // meaningless without a destination), URL-normalize (require
      // scheme so <a href> doesn't treat it as relative).
      const normalized = links
        .map(l => ({ label: (l.label || '').trim(), url: (l.url || '').trim() }))
        .filter(l => l.url)
        .map(l => ({
          label: l.label,
          url: /^https?:\/\//i.test(l.url) ? l.url : `https://${l.url}`,
        }))
      await updateDoc(doc(db, 'neighborhoods', neighborhoodId), {
        footerLinks: normalized,
        updatedAt: serverTimestamp(),
      })
      setToast('Footer links saved.')
      setTimeout(() => setToast(''), 2500)
    } catch (e) {
      console.error('Error saving footer links:', e)
      setError(e?.message || 'Could not save footer links.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading footer links...</p>
      </div>
    )
  }

  return (
    <div className="section">
      <h2>Footer Links</h2>
      <p className="muted" style={{ marginTop: -4, marginBottom: 16 }}>
        Add quick-access external links that appear in the site footer
        for signed-in residents — About page, HOA board, by-laws, etc.
        A <strong>Quick Resources</strong> link to the in-app Resources
        page is added automatically; you don't need to configure it here.
      </p>

      {error && (
        <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>
      )}
      {toast && (
        <div className="success-banner" style={{ marginBottom: 12 }}>{toast}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {links.length === 0 && (
          <p className="muted" style={{ fontStyle: 'italic' }}>
            No links yet. Add one below or start from a set of suggested labels.
          </p>
        )}
        {links.map((row, idx) => (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr auto',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <input
              type="text"
              placeholder="Label (e.g. HOA Board)"
              value={row.label}
              onChange={(e) => updateRow(idx, 'label', e.target.value)}
            />
            <input
              type="url"
              placeholder="https://example.com/board"
              value={row.url}
              onChange={(e) => updateRow(idx, 'url', e.target.value)}
            />
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                type="button"
                className="btn-outline small"
                title="Move up"
                onClick={() => moveRow(idx, -1)}
                disabled={idx === 0}
              >↑</button>
              <button
                type="button"
                className="btn-outline small"
                title="Move down"
                onClick={() => moveRow(idx, 1)}
                disabled={idx === links.length - 1}
              >↓</button>
              <button
                type="button"
                className="btn-outline small danger"
                title="Remove"
                onClick={() => removeRow(idx)}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
        <button type="button" className="btn-outline" onClick={addRow}>
          <Plus size={14} /> Add link
        </button>
        <button type="button" className="btn-outline" onClick={applyDefaults}>
          Populate defaults
        </button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn-primary"
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  )
}


// ============================================================
// Event editor modal — used by both the admin section AND the
// resident Events page (so board members / admins can create
// events without leaving the Events surface). Fully self-contained
// styles so it renders correctly regardless of the parent's CSS.
// ============================================================
export function EventModal({
  editing,
  title, setTitle,
  description, setDescription,
  startAt, setStartAt,
  endAt, setEndAt,
  location, setLocation,
  saving,
  onSave,
  onClose,
}) {
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(15,20,25,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  }
  const card = {
    background: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-xl)', maxWidth: 560, width: '100%',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  }
  const header = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid var(--color-border)',
  }
  const body = {
    display: 'flex', flexDirection: 'column', gap: 14,
    padding: 20, overflowY: 'auto',
  }
  const footer = {
    display: 'flex', justifyContent: 'flex-end', gap: 8,
    padding: 16, borderTop: '1px solid var(--color-border)',
  }
  const label = {
    display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6,
    color: 'var(--color-text-primary)',
  }
  const closeBtn = {
    background: 'transparent', border: 'none', padding: 6,
    cursor: 'pointer', color: 'var(--color-text-secondary)',
    borderRadius: 6, width: 32, height: 32,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <h3 style={{ margin: 0, fontSize: '1.125rem' }}>
            {editing ? 'Edit event' : 'New event'}
          </h3>
          <button style={closeBtn} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div style={body}>
          <div>
            <label style={label}>Title</label>
            <input
              type="text"
              placeholder="e.g. Summer block party"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={label}>Starts</label>
              <input
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
              />
            </div>
            <div>
              <label style={label}>Ends (optional)</label>
              <input
                type="datetime-local"
                value={endAt}
                min={startAt || undefined}
                onChange={(e) => setEndAt(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label style={label}>Location</label>
            <input
              type="text"
              placeholder="e.g. Community park pavilion"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
          <div>
            <label style={label}>Description</label>
            <textarea
              rows={4}
              placeholder="Details residents should know…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <div style={footer}>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create event'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Manage Events admin section
// ============================================================
// Admin/board CRUD over `events/{id}` (see EventsPage.jsx for the
// resident view). Fields: title, description, startAt (Timestamp),
// endAt (Timestamp, optional), location, neighborhoodId, createdBy.
function ManageEventsSection({ userProfile }) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null) // event doc when editing

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startAt, setStartAt] = useState('')  // datetime-local string
  const [endAt, setEndAt] = useState('')
  const [location, setLocation] = useState('')
  const [saving, setSaving] = useState(false)

  const neighborhoodId = userProfile?.neighborhoodId

  const loadEvents = async () => {
    if (!neighborhoodId) { setLoading(false); return }
    try {
      setLoading(true)
      const snap = await getDocs(query(
        collection(db, 'events'),
        where('neighborhoodId', '==', neighborhoodId),
        orderBy('startAt', 'asc')
      ))
      setEvents(snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        startAt: d.data().startAt?.toDate?.() || null,
        endAt: d.data().endAt?.toDate?.() || null,
      })))
    } catch (e) {
      console.error('Error loading events:', e)
      setError(e?.message || 'Could not load events.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadEvents() /* eslint-disable-next-line */ }, [neighborhoodId])

  const openCreate = () => {
    setEditing(null)
    setTitle('')
    setDescription('')
    setStartAt('')
    setEndAt('')
    setLocation('')
    setShowModal(true)
  }

  const openEdit = (event) => {
    setEditing(event)
    setTitle(event.title || '')
    setDescription(event.description || '')
    // Convert Date → datetime-local string (YYYY-MM-DDTHH:MM)
    const toLocalInput = (d) => {
      if (!d) return ''
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
    setStartAt(toLocalInput(event.startAt))
    setEndAt(toLocalInput(event.endAt))
    setLocation(event.location || '')
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditing(null)
  }

  const saveEvent = async () => {
    if (!neighborhoodId) return
    if (!title.trim()) { setError('Title is required.'); return }
    if (!startAt) { setError('Start date/time is required.'); return }
    if (endAt) {
      const startDate = new Date(startAt)
      const endDate = new Date(endAt)
      if (!isNaN(endDate.getTime()) && endDate.getTime() <= startDate.getTime()) {
        setError('End date/time must be after the start date/time.')
        return
      }
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        neighborhoodId,
        title: title.trim(),
        description: description.trim(),
        location: location.trim(),
        startAt: Timestamp.fromDate(new Date(startAt)),
        endAt: endAt ? Timestamp.fromDate(new Date(endAt)) : null,
        updatedAt: serverTimestamp(),
      }
      if (editing) {
        await updateDoc(doc(db, 'events', editing.id), payload)
      } else {
        await addDoc(collection(db, 'events'), {
          ...payload,
          createdBy: userProfile?.email || userProfile?.uid || null,
          createdAt: serverTimestamp(),
        })
      }
      closeModal()
      loadEvents()
    } catch (e) {
      console.error('Error saving event:', e)
      setError(e?.message || 'Could not save event.')
    } finally {
      setSaving(false)
    }
  }

  const removeEvent = async (event) => {
    if (!window.confirm(`Delete "${event.title || 'this event'}"? This cannot be undone.`)) return
    try {
      await deleteDoc(doc(db, 'events', event.id))
      loadEvents()
    } catch (e) {
      console.error('Error deleting event:', e)
      setError(e?.message || 'Could not delete event.')
    }
  }

  const fmtRange = (start, end) => {
    if (!start) return 'Date TBD'
    const startStr = start.toLocaleString([], {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })
    if (!end) return startStr
    const sameDay = start.toDateString() === end.toDateString()
    const endStr = end.toLocaleString([], sameDay
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    return `${startStr} – ${endStr}`
  }

  return (
    <div className="section">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Manage Events</h2>
        <button className="btn-add-circle" data-walk="event-add-btn" onClick={openCreate} title="New event" aria-label="New event">
          <Plus size={20} />
        </button>
      </div>
      <p className="muted" style={{ marginTop: -8, marginBottom: 16 }}>
        Schedule events for residents — block parties, board meetings, garage sales.
        Residents view them at <strong>Events</strong> in the top navigation.
      </p>

      {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div className="loading-state"><div className="spinner" /><p>Loading events…</p></div>
      ) : events.length === 0 ? (
        <div className="form-card" style={{ textAlign: 'center' }}>
          <p className="muted">No events yet. Click + to schedule one.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {events.map(event => (
            <div key={event.id} className="form-card" style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 16,
              padding: 16,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: 0, fontSize: '1.0625rem' }}>{event.title || 'Untitled event'}</h3>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.875rem' }}>
                  {fmtRange(event.startAt, event.endAt)}
                  {event.location && <> · {event.location}</>}
                </p>
                {event.description && (
                  <p style={{ margin: '8px 0 0', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>
                    {event.description}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  className="announcement-action-btn"
                  onClick={() => openEdit(event)}
                  title="Edit event"
                >
                  <Edit size={16} />
                </button>
                <button
                  type="button"
                  className="announcement-action-btn danger"
                  onClick={() => removeEvent(event)}
                  title="Delete event"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <EventModal
          editing={editing}
          title={title} setTitle={setTitle}
          description={description} setDescription={setDescription}
          startAt={startAt} setStartAt={setStartAt}
          endAt={endAt} setEndAt={setEndAt}
          location={location} setLocation={setLocation}
          saving={saving}
          onSave={saveEvent}
          onClose={closeModal}
        />
      )}
    </div>
  )
}


export default AdminPage
